// The playable board.
//
// Holds one actor per card, springs each toward the transform `layout` asks for,
// and turns clicks into engine actions. The scene never decides what is legal:
// it asks the session for legal actions and only ever offers those, so a card
// that cannot be played simply has nothing to click.

import { CardLibrary } from '../cards/library.ts';
import { DOMAIN_COLORS, type Domain } from '../cards/types.ts';
import type { GpuContext } from '../gfx/device.ts';
import {
  DEFAULT_ENVIRONMENT, DEFAULT_GRADE, Renderer,
  type CardDraw, type Environment, type Grade, type Scene,
} from '../gfx/renderer.ts';
import {
  clamp, compose, damp, lookAt, mat4, multiply, normalize, perspectiveReverseZ,
  type Mat4, type Vec3,
} from '../gfx/math.ts';
import { foilTier } from '../cards/types.ts';
import { CARD } from '../gfx/mesh.ts';
import {
  ARRANGE_GRID, BATTLEFIELD_ZONE, CAMERA, CAMERA_PITCH, battlefieldAnchor, setViewMode,
  unitScale,
  cellAt, insideZone, layout, tableLayout, zoneFor,
  type ArrangeZone, type Slot,
} from './layout.ts';
import { findPaymentPath, playsAfterPaying } from './payment.ts';
import { tapIsDestructive } from '../rules/state.ts';
import { TurnBanner } from './banner.ts';
import { paintPlaymat } from './playmat.ts';

import { abilitiesOn, zoneList } from '../rules/state.ts';
import { primaryActionFrom } from './session.ts';
import type { Match } from './match.ts';
import { aimArrow, type Arrow } from './aim.ts';
import { groupMove, joinable, type Move, sharedDestinations } from './group.ts';
import { DOMAINS, sameLocation } from '../rules/types.ts';
import { legalActions } from '../rules/engine.ts';
import type { Action, Location, ObjectId } from '../rules/types.ts';

/** How high a dragged card rides above the table. */
const DRAG_LIFT = 0.55;

/**
 * How far a card dragged out of hand has to travel before it is drawn at the
 * size it will be on the table.
 *
 * A hand card is drawn larger than a card in play, because it is nearer and
 * has to be readable. Carrying that size onto the mat made a dragged card look
 * like it belonged to a different set -- it shrinks on the way instead, which
 * is also what tells the player it is going somewhere.
 */
const DRAG_SHRINK_OVER = 2.2;

/**
 * Past this, toward the player, is where the hand is held.
 *
 * A card released out here is going home: nothing is played and nothing is
 * paid. It sits beyond the runes strip (z 2.28) and short of the hand row
 * itself (3.05), so the whole near edge of the table reads as "put it back".
 */
const HAND_RETURN_Z = 2.62;

/**
 * What a card in hand takes of the direct key and of the ambient dome.
 *
 * Tuned against a measurement rather than by eye: the mean luminance of the
 * hand's artwork should land on the board's, which is the half that already
 * looked right. Sampled off the rendered frame the hand read 0.46 against the
 * board's 0.29 -- 1.6x -- and washed out; at 0.70 the two sit at 0.438 and
 * 0.442. The bloom was part of the evidence: with the hand undamped the *board*
 * sample rose by 0.09, because a row of over-lit cards along the bottom of the
 * screen was lifting the whole frame.
 *
 * `ambient` measured very nearly inert here -- 0.6 to 1.0 moved the hand by
 * 0.003, the dome contributing little beside the key. Kept because it costs a
 * float that was padding anyway and would matter under a brighter dome, but
 * `direct` is the knob that does the work.
 *
 * **A card in hand still reads too light in its black frame**, and the dome is
 * not why. Measured on the darkest tenth of each card rather than the mean, a
 * hand card sits at 0.10 to 0.26 against 0.02 to 0.08 for cards on the table --
 * but taking `ambient` from 1.0 to 0.35 moved that by 0.002, so whatever lifts
 * those blacks is somewhere else. The remaining suspects are the wrapped
 * diffuse and the paper sheen, both of which are additive and both of which
 * peak on a card held square-on to the key and the camera, which is exactly
 * what a hand card is. Isolating it needs the *same card* rendered in both
 * places, not two different cards compared across a screenshot.
 */
const HAND_LIGHT = { direct: 0.70, ambient: 1.0 };

/**
 * The foil is off on the *board*, on every platform, until it is reworked.
 *
 * Not deleted, and not ripped out of the shader: `tier` already gates every
 * foil pass in `card.wgsl`, so sending 0 turns off the trim specular, the
 * holographic layer, the glitter and the grazing-angle wash together, and
 * turning them back on is this one line. Deleting the shader code instead
 * would throw away the thing that is meant to be reworked.
 *
 * The showcase (`?showcase`) is deliberately untouched -- it reads the tier
 * from the card, not from here. It is the tool the rework will be done with,
 * so it is the one place the foil still has to run.
 */
const BOARD_FOIL = false;

/**
 * Whether Discord's own chrome is over this page.
 *
 * Read off the class `markDiscordChrome` puts on the root element rather than
 * by importing the Activity module: the CSS that steps around that chrome
 * reads the same class, so the layout and the camera cannot come to different
 * conclusions about which corner belongs to us.
 */
function inDiscordChrome(): boolean {
  return typeof document === 'object'
    && document.documentElement.classList.contains('in-discord');
}

/** What each pile is called under the card that stands for it. */
const PILE_LABEL: Record<string, string> = {
  mainDeck: 'deck', runeDeck: 'runes', trash: 'trash',
};

/** Card half-extents, mirroring src/gfx/mesh.ts. */
const HALF_W = 0.315;
const HALF_H = 0.44;

interface Actor {
  id: ObjectId;
  card: string;
  slot: Slot;
  pos: Vec3;
  rot: Vec3;
  scale: number;
  hover: number;
  lift: number;
  highlight: number;
  seed: number;
  model: Mat4;
  /** Set when the actor's slot disappeared; it fades and is removed. */
  retiring: boolean;
}

/**
 * A thing the DOM overlay draws over the table.
 *
 * The scene draws cards; everything that has to stay crisp and legible at any
 * distance -- a unit's Might, the outline of somewhere you may drop it -- is
 * text and lines, which is what the DOM is for. Positions are normalised to
 * the canvas so the overlay never has to know about device pixel ratio.
 */
export type OverlayItem =
  /** Live stats on a card that is on the table. */
  | {
    kind: 'stats';
    id: ObjectId;
    /** Centre of the card, 0..1 across the canvas. */
    at: [number, number];
    /** On-screen height of the card, 0..1, so the badge scales with it. */
    size: number;
    might: number;
    /** Printed Might, to say whether the number has been changed. */
    printed: number;
    /** Whether this card prints a Might at all; gear and runes do not. */
    hasMight: boolean;
    damage: number;
    /** Short marks for statuses worth seeing at a glance. */
    marks: string[];
    mine: boolean;
  }
  /**
   * 108. How deep a pile is, or how many cards a hand holds.
   *
   * On the table beside the thing it counts, rather than in a rail off to one
   * side: one card is drawn for a whole deck, so the pile cannot say its own
   * depth, and a number kept anywhere else is a second reading of the board
   * that can disagree with the first.
   */
  | {
    kind: 'count';
    /** Stable across frames, so the node is reused rather than rebuilt. */
    key: string;
    at: [number, number];
    /** On-screen height of the card it belongs to, 0..1. */
    size: number;
    value: number;
    label: string;
    mine: boolean;
    /** A deck close to running out is worth noticing. */
    low: boolean;
  }
  /**
   * 327/355. What something waiting on the chain is pointing at.
   *
   * The chain is the one part of the game with no representation on the table:
   * a spell sits there with its targets already chosen and nothing says which
   * cards they are, so a played removal spell is a mystery until it resolves
   * and something dies. The arrow is drawn from the card that is waiting to
   * each thing it named.
   *
   * The geometry arrives in pixels and leaves normalised, because the overlay
   * SVG is stretched (`preserveAspectRatio="none"`): anything shaped in its
   * coordinate space -- an arrowhead above all -- comes out skewed by whatever
   * the window's aspect happens to be.
   */
  | {
    kind: 'aim';
    /** Stable across frames, so it can be faded in and out rather than popping. */
    key: string;
    /** The curve, already flattened into points. */
    shaft: [number, number][];
    /** The head, as a triangle. */
    head: [number, number][];
    /** Whose item it is: yours reads as intent, theirs as a threat. */
    mine: boolean;
    /** 0 while arriving, 1 once settled, back to 0 on the way out. */
    appear: number;
    /** Seconds, for the dashes that travel along the line. */
    time: number;
  }
  /** Somewhere a pending choice will accept a click. */
  /**
   * 164.2. A rune this play is about to spend, and how.
   *
   * The two taps are not interchangeable and the board never said which was
   * coming: exhausting yields Energy and the rune stays, recycling yields
   * Power and the rune is gone to the Rune Deck for the rest of the game.
   * Since costs are paid automatically, the first a player knew of a recycle
   * was a rune missing afterwards.
   */
  | {
    kind: 'cost';
    key: string;
    points: [number, number][];
    /** True for a recycle: the rune is destroyed, not merely turned. */
    destructive: boolean;
    appear: number;
  }
  | {
    kind: 'drop';
    key: string;
    /** The quad's four corners, normalised, in order. */
    points: [number, number][];
    /** Under the pointer right now, eased rather than switched. */
    hot: number;
    label: string;
    /** 0 while arriving, 1 once settled, back to 0 on the way out. */
    appear: number;
  };

/** One thing a clicked card can do. A `null` action means "move". */
export interface MenuItem {
  label: string;
  sub: string;
  action: Action | null;
}

/** The gesture menu for a card that offers more than one. */
export interface CardMenu {
  object: ObjectId;
  title: string;
  items: MenuItem[];
}

/** "1 energy, 1 power" for a menu row, or the empty string for a free ability. */
function costLabel(cost: { energy?: number; power?: number } | undefined): string {
  if (!cost) return '';
  const parts: string[] = [];
  if (cost.energy) parts.push(`${cost.energy} energy`);
  if (cost.power) parts.push(`${cost.power} power`);
  return parts.join(', ');
}

export interface BoardCallbacks {
  /** Card under the pointer, for the detail panel. */
  onHover(card: string | null): void;
  /** Called whenever the visible game state changes. */
  onState(): void;
  /** A short line describing what the board is waiting for. */
  onPrompt(text: string): void;
  /** Everything the DOM overlay should draw this frame. */
  onOverlay(items: OverlayItem[]): void;
  /** A pile the player asked to look through, or null to close it. */
  onInspect(view: { title: string; cards: string[] } | null): void;
  /**
   * The wheel turned over the board, in pixels.
   *
   * The card being read is under the pointer and the text describing it is in
   * the panel on the left, so scrolling it meant moving the mouse off the card
   * -- which on a hover-driven panel changes what is being read. Forwarded
   * through the callbacks rather than reaching into the panel from here: the
   * board owns the canvas and the HUD owns its own scrolling.
   *
   * Returns whether the panel used it, so the board only swallows a scroll
   * that actually did something.
   */
  onScroll(deltaY: number): boolean;
}

type Pending =
  | { kind: 'none' }
  /** A unit is in hand and needs a destination chosen (355.2). */
  | { kind: 'placeUnit'; object: ObjectId; options: Action[] }
  /**
   * 144.3. Units selected to move together, awaiting a shared destination.
   *
   * A list rather than one unit, because a standard move is "one game action
   * performed on multiple Units" and moving them one at a time is not the same
   * thing: the first move ends in a cleanup that stages the combat, and
   * 144.1.c forbids a standard move during one. A player who sent a single
   * unit into a contested battlefield had committed it alone and could not
   * follow it with the rest of their army.
   */
  | { kind: 'moveUnit'; objects: ObjectId[]; options: Action[] }
  /** 811. Hiding a card face down at a battlefield you control. */
  | { kind: 'hideCard'; object: ObjectId; options: Action[] }
  /**
   * 355.8. A card or ability is aimed and the player has to say where.
   *
   * The engine settles targets when it enumerates the action, because they are
   * priced -- so it offers one action per target and picking the action is
   * picking the target. This is the click that does the picking.
   */
  | { kind: 'chooseTarget'; object: ObjectId; options: Action[] };

/**
 * Where the turn plaque stands: on the centre line between the two boards,
 * lifted clear of the mat and tilted up toward the camera.
 */
// Off to one side of the centre line rather than on it: the middle of the
// table is where the battlefields are, and a plaque there covers the game.
// To the right of the battlefields, clear of their rectangles (which reach to
// x 2.20) and of the Main Deck at 3.16. It sat on the left, where the card
// panel already is.
const BANNER_AT: Vec3 = [2.62, 0.18, 0];
/** Square-on to the camera, the same way the hands are (see CAMERA_PITCH). */
const BANNER_TILT = -CAMERA_PITCH - 0.08;
const BANNER_WIDTH = 1.46;

/** 315-318. The phases, in the words the rules use for them. */
const PHASE_NAME: Record<string, string> = {
  awaken: 'Awaken', beginning: 'Beginning', channel: 'Channel',
  draw: 'Draw', main: 'Main', ending: 'Ending',
};

export class Board {
  private readonly gpu: GpuContext;
  private readonly renderer: Renderer;
  private readonly library: CardLibrary;
  private readonly session: Match;
  private readonly callbacks: BoardCallbacks;

  private readonly actors = new Map<ObjectId, Actor>();
  private readonly env: Environment;
  private readonly grade: Grade;
  /** 316. The turn plaque, standing on the table between the two boards. */
  private readonly banner: TurnBanner;

  private pointer: [number, number] = [0, 0];
  /**
   * The card currently under the pointer's control, and where on the table it
   * is being held.
   *
   * A drag used to be a click with extra steps: the gesture worked, but the
   * card never left its slot, so there was nothing to aim and nothing to see.
   * While this is set the actor is driven by the pointer instead of by its
   * layout slot, which is what makes it a drag.
   */
  private dragging: { id: ObjectId; at: Vec3 } | null = null;
  /**
   * A play carried out of the hand but not yet made.
   *
   * Pressing a card used to play it outright, so a drag cast the spell the
   * instant it began and the drop had nothing left to decide. Nothing is
   * committed now until the card is released over somewhere that will take it
   * -- including the runes, which is the half that mattered: `path` is the
   * payment the play needs, and a drag called off never spends it (167.1).
   */
  private carry: { object: ObjectId; path: Action[] } | null = null;
  /**
   * The order the player has put their own hand in.
   *
   * Local and never sent: a hand is secret, so there is no second view of it to
   * keep in step, and how someone likes their cards sorted is not something an
   * opponent is entitled to know. Ids that are no longer in hand are kept
   * rather than pruned -- a card played and returned goes back where it was.
   */
  private handOrder: ObjectId[] = [];
  /**
   * How much light a card held in hand takes.
   *
   * A hand is tilted to face the camera, which puts its cards square-on to the
   * key and near the specular peak at the same time: measured off the rendered
   * frame, the hand came out 1.6x brighter than the same artwork lying on the
   * table and washed out. The fix belongs on the held cards, not on the light
   * -- the light is right, and dimming it would take the board down too.
   */
  handLight = { ...HAND_LIGHT };
  /**
   * Whether the gesture in progress is a finger rather than a mouse.
   *
   * Decided per press, not once for the session: a laptop with a touchscreen
   * has both, and making every later mouse click need two because the player
   * once tapped the screen would be worse than the problem being solved. What
   * it changes is real either way -- a finger has no hover, so there is no way
   * to look at a card without committing to it.
   */
  private touchMode = false;
  /**
   * The card a tap has selected, on touch.
   *
   * A mouse has a hover, and the whole card-reading half of this interface is
   * built on it: point at a card and the panel shows it. A finger has no such
   * state -- there is only "not touching" and "touching" -- so a tap that
   * commits leaves no way to *look* at a card at all. Every tap played the
   * card it landed on. The first tap selects and shows; the second commits.
   */
  private picked: ObjectId | null = null;
  private hovered: ObjectId | null = null;
  /**
   * The last card either side played, for the panel to fall back to.
   *
   * A spell that resolves and leaves is gone before it can be read, and the
   * opponent's plays are the ones worth reading -- so the panel shows the last
   * one played whenever the player is not looking at something themselves.
   * It is only ever a *fallback*: hovering, or tapping on a touchscreen, wins
   * immediately, so this can never take the panel away from something being
   * read.
   */
  private announced: string | null = null;
  /** How far through `state.events` the announcement has looked. */
  private eventsSeen = 0;
  /** 117.1. Cards set aside during the mulligan, at most two. */
  private readonly mulliganPicks = new Set<ObjectId>();
  /**
   * Cards the player could actually play right now, counting resources they
   * could still produce by tapping runes. Recomputed on every state change so
   * the glow means "you can play this", not merely "this is yours".
   */
  private affordable = new Set<ObjectId>();
  private pending: Pending = { kind: 'none' };
  /**
   * The gesture menu for a card that can do more than one thing.
   *
   * Read by the HUD, which draws it in the same panel as an engine question --
   * they are never both open, because the engine only asks while it is waiting
   * on an answer and the menu only opens on a click the player made.
   */
  menu: CardMenu | null = null;
  /**
   * 318. Whether a card in hand keeps its idle flex. A player setting: the
   * motion is life on a held card and drift to anyone who does not want it.
   */
  cardMotion = true;
  /** 164.2. Whether a click on a rune taps it. Off by default; see Settings. */
  manualRunes = false;
  /** Whether the click being handled asked to hide rather than to play. */
  private hideModifier = false;
  /** Locations the player may currently click, while `pending` is active. */
  private dropTargets: { location: Location; action: Action }[] = [];
  /**
   * Objects the player may currently click to aim a pending card or ability.
   *
   * Kept beside `dropTargets` rather than folded into it because a target is
   * an object and a destination is a place: an enemy unit standing at a
   * battlefield is a different click from the battlefield it stands on.
   */
  private targetChoices = new Map<ObjectId, Action>();
  /**
   * 144.4.b. The base a pending move will accept, which is a *place* and not a
   * card: nothing stands there to be clicked.
   *
   * Without this there was no way to say "back to my base" at all -- the engine
   * offered the move, `pick` had no object to return, and the click fell
   * through to cancelling the whole gesture.
   */
  private hoveredSpot = -1;
  /**
   * How present each overlay shape is, keyed by what it belongs to.
   *
   * The cards themselves have been damped since the beginning; the shapes over
   * them were not, so a target outline snapped into existence and a targeting
   * arrow arrived fully drawn. Both read as the interface changing state
   * rather than as something happening on the table.
   *
   * The last geometry is kept with the value, because a shape still has to be
   * drawn on the way *out* -- by then whatever produced it is already gone.
   */
  private readonly presence = new Map<string, { v: number; hot: number; item: OverlayItem }>();
  /**
   * Which runes the card being considered would spend, and how.
   *
   * Recomputed only when the card changes, never per frame: the search runs on
   * a `structuredClone` of the whole state, which is the same ~11ms the AI
   * pays per candidate and far too much to spend sixty times a second.
   */
  /** 144.3. Units that could be added to the move being composed. */
  private moveJoinable = new Set<ObjectId>();
  private costPreview: { id: ObjectId; destructive: boolean }[] = [];
  private costPreviewFor: ObjectId | null = null;
  /** The frame's delta, for the damping inside `overlay()`. */
  private frameDt = 1 / 60;
  /** A pile the player is looking through; not game state, so it lives here. */
  private inspecting: { title: string; cards: string[] } | null = null;
  /**
   * A press that has not been released yet, and where it started.
   *
   * Dragging and clicking are the same gesture in this scene: pressing a card
   * begins whatever clicking it begins, and releasing over a target finishes
   * it. So a drag needs no machinery of its own -- it reuses the pending
   * choice the press already opened, which is also why click-then-click still
   * works for anyone who prefers it.
   */
  /**
   * The gesture in progress.
   *
   * `opened` says this press put an offer on screen -- highlighted places, a
   * carried card -- so the release knows whether it is finishing a gesture it
   * started or one that was already running. `hadPending` says a choice was
   * already open when the press landed, which is the second click of a
   * click-then-click.
   */
  private press: {
    id: ObjectId | null; at: [number, number]; moved: boolean;
    opened: boolean; hadPending: boolean;
  } | null = null;

  private readonly proj = mat4();
  private readonly view = mat4();
  private readonly viewProj = mat4();
  /**
   * Live camera rig. Mutate `camera` in the console to reframe the table; once
   * a framing looks right, move the numbers into these defaults.
   */
  // The rig lives in layout.ts, which derives the hand tilts from its pitch.
  /**
   * This board's own copy of the rig.
   *
   * Filled in the constructor body, *after* `setViewMode` -- a class field
   * initializer runs before the body, so copying it here took the rig as the
   * module declared it and the view mode was applied to a table nobody was
   * looking at. Desktop kept the phone's framing for exactly that reason.
   */
  readonly camera = { ...CAMERA };
  private cameraPos: Vec3 = [0, 5.35, 5.35];
  private cameraTarget: Vec3 = [0, 0, 0.10];

  private running = false;
  private raf = 0;
  private lastTime = 0;
  private time = 0;
  private readonly detach: Array<() => void> = [];

  constructor(
    gpu: GpuContext, renderer: Renderer, library: CardLibrary,
    session: Match, callbacks: BoardCallbacks,
  ) {
    // Before anything reads the table's shape -- the mat is painted from the
    // same rectangles, so this cannot be decided later. Keyed on the pointer
    // rather than the window: a desktop window dragged narrow is still a
    // desktop, and giving it the phone's framing (board pushed right, card art
    // hidden) made it worse for no reason.
    // `(pointer: coarse)` alone is not the test: a laptop with a touchscreen
    // reports coarse while having a mouse, a hover and a large screen, and it
    // wants the desktop table. Adding `hover: none` is what distinguishes a
    // device that has *only* a finger.
    // `?view=compact` forces the phone's table on a desktop, which is the only
    // way to see what a phone sees without one in your hand.
    const forced = typeof location === 'object'
      ? new URLSearchParams(location.search).get('view') : null;
    const compact = forced === 'compact' || (forced !== 'desktop'
      && typeof matchMedia === 'function'
      && matchMedia('(pointer: coarse) and (hover: none)').matches);
    // Inside Discord the right-hand corner belongs to Discord's own controls,
    // so the table is not pushed as far into it. Everywhere else that edge is
    // ours and the board takes the room.
    setViewMode(compact ? 'compact' : 'desktop', !inDiscordChrome());

    Object.assign(this.camera, CAMERA);

    this.gpu = gpu;
    this.renderer = renderer;
    this.library = library;
    this.session = session;
    this.callbacks = callbacks;

    this.env = { ...DEFAULT_ENVIRONMENT };
    // The table wants a broader, flatter light than the showcase: cards lie
    // face up under it, so a low key would rake across all of them at once.
    this.env.lightDir = [0.15, 0.86, 0.48];
    this.env.lightIntensity = 2.10;
    // A card game is read, not admired: the mat and the cards at the edges of
    // the table have to be as legible as the ones in the middle. The ambient
    // is what fills the zones the key light rakes past.
    this.env.ambientMix = 0.38;
    this.env.skyColor = [0.15, 0.19, 0.29];
    this.env.bgDust = 0.03;
    this.grade = { ...DEFAULT_GRADE };
    // 0.55 was fog: it crushed the corners, and the corners are where the
    // decks, the trash and half the runes live.
    this.grade.vignette = 0.20;

    this.banner = new TurnBanner(gpu.device, library.mipmaps);

    // The mat is printed once, from the same zone table the slots come from.
    // Asynchronous, and the renderer draws unprinted felt until it lands.
    const mat = tableLayout(session.state, session.human);
    void paintPlaymat(
      gpu.device, library.mipmaps,
      mat.battlefieldZ, mat.battlefieldSpacing, mat.battlefieldCount,
    ).then((tex) => renderer.setPlaymat(tex));

    session.onChange = () => {
      this.sync();
      this.callbacks.onState();
      this.callbacks.onPrompt(this.prompt());
    };

    this.sync();
    this.attachInput();
  }

  // -- lifecycle -------------------------------------------------------------

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    const loop = (now: number) => {
      if (!this.running) return;
      const dt = Math.min((now - this.lastTime) / 1000, 1 / 15);
      this.frameDt = dt;
      this.lastTime = now;
      this.time += dt;
      this.update(dt);
      this.draw();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  destroy(): void {
    this.stop();
    this.banner.destroy();
    for (const off of this.detach) off();
    this.detach.length = 0;
  }

  // -- state -> actors -------------------------------------------------------

  /** Rebuilds the actor set from the current game state. */
  private sync(): void {
    const state = this.session.state;

    // 355. What was played since the last look. Read off the event stream
    // rather than the board, because the interesting case is a spell -- which
    // is on the chain for a moment and then gone, and never sits anywhere the
    // player could point at.
    //
    // A rebuilt stream (a new match, or a remote frame that starts over)
    // shows up as the log getting *shorter*; starting again from the end
    // rather than from zero keeps a fresh match from announcing the whole
    // opening as if it had just happened.
    if (state.events.length < this.eventsSeen) this.eventsSeen = state.events.length;
    for (let i = this.eventsSeen; i < state.events.length; i++) {
      const event = state.events[i]!;
      if (event.t === 'play') this.announced = event.card;
    }
    this.eventsSeen = state.events.length;
    const slots = layout(state, this.session.rules, {
      human: this.session.human,
      // Where both players have put the cards in their bases. It comes from
      // the match, not from here, so a remote game draws the arrangement the
      // server holds and a local one draws its own.
      arrangement: this.session.arrangement,
      handOrder: this.handOrder,
    });
    const seen = new Set<ObjectId>();

    for (const slot of slots) {
      seen.add(slot.object);
      let actor = this.actors.get(slot.object);
      if (!actor) {
        actor = {
          id: slot.object, card: slot.card, slot,
          // New cards animate in from their owner's edge of the table rather
          // than popping into existence mid-board.
          pos: [slot.position[0], slot.position[1] + 0.6, slot.position[2] + (slot.owner === this.session.human ? 1.2 : -1.2)],
          rot: [...slot.rotation] as Vec3,
          scale: slot.scale * 0.8,
          hover: 0, lift: 0, highlight: 0,
          seed: hashSeed(slot.object),
          model: mat4(), retiring: false,
        };
        this.actors.set(slot.object, actor);
      }
      actor.slot = slot;
      actor.card = slot.card;
      actor.retiring = false;
    }

    for (const [id, actor] of this.actors) {
      if (!seen.has(id)) actor.retiring = true;
    }

    this.recomputeAffordable();
  }

  /**
   * Works out which cards are reachable, including after tapping runes. Doing
   * this once per state change rather than per frame keeps it off the hot path;
   * a hand is under ten cards and each search is bounded.
   */
  private recomputeAffordable(): void {
    this.affordable.clear();
    if (!this.session.waitingOnHuman) return;

    const actions = this.session.humanActions();
    for (const a of actions) {
      if (a.t === 'play') this.affordable.add(a.object);
      if (a.t === 'move') for (const o of a.objects) this.affordable.add(o);
      if (a.t === 'activate') this.affordable.add(a.object);
    }

    for (const actor of this.actors.values()) {
      const slot = actor.slot;
      if (slot.role !== 'hand' && slot.role !== 'champion') continue;
      if (slot.owner !== this.session.human) continue;
      if (this.affordable.has(actor.id)) continue;
      if (this.findPaymentPath(actor.id)) this.affordable.add(actor.id);
    }
  }

  private update(dt: number): void {
    const rate = 11;

    for (const [id, actor] of this.actors) {
      const slot = actor.slot;

      if (actor.retiring) {
        // Sink and shrink, then drop out of the scene.
        actor.scale = damp(actor.scale, 0, 9, dt);
        actor.pos[1] = damp(actor.pos[1], -0.5, 9, dt);
        if (actor.scale < 0.02) this.actors.delete(id);
        continue;
      }

      const hovered = this.hovered === id;
      const selected = this.inPendingMove(id)
        || (this.pending.kind !== 'none' && this.pending.kind !== 'moveUnit'
          && this.pending.object === id)
        || this.mulliganPicks.has(id);
      const wantHover = hovered || selected ? 1 : 0;
      actor.hover = damp(actor.hover, wantHover, 12, dt);

      // Hand cards rise toward the camera on hover; board cards just lift.
      const lift = slot.role === 'hand' ? actor.hover * 0.26 : actor.hover * 0.10;
      actor.lift = damp(actor.lift, lift, 14, dt);

      const playable = slot.interactive && this.session.waitingOnHuman
        && this.affordable.has(actor.id);
      actor.highlight = damp(actor.highlight, playable ? 1 : 0, 8, dt);

      // A dragged card is held by the pointer, lifted clear of the table so it
      // reads as picked up and passes over whatever it is being dragged across.
      const drag = this.dragging?.id === id ? this.dragging.at : null;
      const target: Vec3 = drag ? [drag[0], DRAG_LIFT, drag[2]] : slot.position;
      // Snappier while held: a dragged card that lags the cursor feels broken
      // rather than weighty.
      const posRate = drag ? 26 : rate;

      for (let i = 0; i < 3; i++) {
        actor.pos[i] = damp(actor.pos[i]!, target[i]!, posRate, dt);
        // 414.1.a. A rune about to be exhausted leans toward the ninety
        // degrees it is about to turn -- a third of the way, which reads as
        // "this one is going" without pretending it has already happened. A
        // recycle is not previewed this way: the rune is not being turned, it
        // is being destroyed, and the outline says so instead.
        const leaning = i === 1 && !slot.exhausted
          && this.costPreview.some((c) => c.id === id && !c.destructive);
        const want = leaning ? slot.rotation[i]! - Math.PI / 2 * 0.34 : slot.rotation[i]!;
        actor.rot[i] = damp(actor.rot[i]!, want, rate, dt);
      }
      // A card carried out of the hand shrinks toward the size it will be once
      // it is on the table. A hand card is drawn larger because it is close and
      // has to be readable; sailing across the mat at that size, it read as a
      // different set of cards -- and gave no sign it was going anywhere.
      // Blended by distance travelled, so it is the movement that does it.
      let targetScale = slot.scale;
      if (drag) {
        if (slot.role === 'hand') {
          const gone = Math.hypot(drag[0] - slot.position[0], drag[2] - slot.position[2]);
          targetScale = slot.scale
            + (unitScale() - slot.scale) * clamp(gone / DRAG_SHRINK_OVER, 0, 1);
        }
        // No hover bump on a card that is already in your hand. The bump is
        // there to answer "you are pointing at this"; on a dragged card the
        // pointer is always on it, and at +22% for a hand card it more than
        // cancelled the shrink -- the card left the hand and got *bigger*.
        targetScale *= 1.04;
      } else {
        targetScale *= 1 + actor.hover * (slot.role === 'hand' ? 0.22 : 0.06);
      }
      actor.scale = damp(actor.scale, targetScale, rate, dt);
    }

    // A gentle drift keeps the table from looking like a static render.
    const sway = Math.sin(this.time * 0.15) * this.camera.sway;
    // The pan moves eye and target together: a slide, not a swing.
    const pan = this.camera.panX;
    this.cameraPos = [sway + pan, this.camera.height, this.camera.distance];
    this.cameraTarget = [pan, 0, this.camera.targetZ];
    perspectiveReverseZ(this.camera.fov, this.gpu.aspect, 0.05, this.proj);
    lookAt(this.cameraPos, this.cameraTarget, [0, 1, 0], this.view);
    multiply(this.proj, this.view, this.viewProj);

    // A drop target has to answer the pointer, or there is no way to tell a
    // place you may click from a place you may not until you have clicked it.
    const spots = this.dropSpots();
    this.hoveredSpot = spots.findIndex((d) => pointInQuad(this.pointer, d.corners));

    // With a finger there is no hover to read, so the selection stands in for
    // one: what the player last tapped is what the detail panel shows, and it
    // stays there after the finger has gone.
    this.hovered = this.touchMode ? this.picked : this.pick(this.pointer, false);
    // 108. What may be *read* is not what may be *tapped*, and on a
    // touchscreen the two had been folded together.
    //
    // `pick(.., false)` -- the hover set -- already refuses a face-down card,
    // which is why a mouse could never read one. But with a finger there is no
    // hover, so the panel follows `picked`, and `picked` comes from the *click*
    // set, which deliberately includes the piles so a deck can be tapped for
    // its count. The result was that tapping either Main Deck or either Rune
    // Deck put the card on top of it into the panel -- the next draw, and the
    // opponent's next draw, both readable by anyone with a finger.
    //
    // The gate goes here rather than on `picked`, because `picked` is also the
    // selection a gesture is built from and a deck must stay tappable.
    const looked = this.hovered !== null ? this.actors.get(this.hovered) : null;
    const hoveredActor = looked && !looked.slot.faceDown ? looked : null;
    // Whatever is being looked at, or failing that the last card played. The
    // order is the whole of the rule: a card under the pointer always wins, so
    // the panel never changes out from under someone reading it.
    this.callbacks.onHover(hoveredActor ? hoveredActor.card : this.announced);
    this.callbacks.onOverlay(this.overlay());
  }

  /**
   * What the DOM overlay draws over the table this frame.
   *
   * Built here because this is where the projection is: the scene already
   * knows where every card ended up on screen, and asking a second time from
   * the HUD would be a second answer that could disagree with the first.
   */
  private overlay(): OverlayItem[] {
    const items: OverlayItem[] = [];
    const { width, height } = this.gpu;
    const norm = (p: [number, number]): [number, number] =>
      [p[0] / width, p[1] / height];

    // Somewhere to drop what is in hand, or the unit being moved. Battlefields
    // are cards and carry their own footprint; a base is a printed rectangle.
    // Collected first and emitted at the end, so that a shape which has just
    // stopped being offered can still be drawn while it fades.
    const shapes = new Map<string, { item: OverlayItem; hot: boolean }>();

    const offered = this.dropIds();
    const spots = this.dropSpots();
    spots.forEach((spot, i) => {
      shapes.set(`spot:${spot.label}:${i}`, {
        hot: i === this.hoveredSpot,
        item: {
          kind: 'drop',
          key: `spot:${spot.label}:${i}`,
          points: spot.corners.map(norm),
          hot: 0,
          label: spot.label,
          appear: 0,
        },
      });
    });

    for (const actor of this.actors.values()) {
      if (actor.retiring) continue;
      const slot = actor.slot;

      if (offered.has(actor.id)) {
        const corners = this.cornersOf(actor);
        if (corners) {
          shapes.set(`drop:${actor.id}`, {
            hot: this.hovered === actor.id,
            item: {
              kind: 'drop',
              key: `drop:${actor.id}`,
              points: corners.map(norm),
              hot: 0,
              // 144.3. A move being composed puts three different kinds of
              // outline on the table at once, and calling all of them "Target"
              // is why a player could not tell that sending a second unit was
              // even possible. Each says what it is instead.
              label: slot.role === 'battlefield' ? 'Battlefield'
                : this.inPendingMove(actor.id) ? 'Going'
                  : this.moveJoinable.has(actor.id) ? '+ Send too'
                    : 'Target',
              appear: 0,
            },
          });
        }
      }

      // 108. A pile says how deep it is, under the card that stands for it.
      // 442. XP is a resource nothing on the table shows, so it rides on the
      // legend -- the card it belongs to -- rather than having a rail of its
      // own.
      if (slot.pile || slot.role === 'legend') {
        const corners = this.cornersOf(actor);
        const centre = this.project([actor.pos[0], actor.pos[1], actor.pos[2]]);
        if (corners && centre[2] >= 0) {
          const top = Math.min(...corners.map((c) => c[1]));
          const bottom = Math.max(...corners.map((c) => c[1]));
          const mine = slot.owner === this.session.human;
          if (slot.pile) {
            items.push({
              kind: 'count',
              key: `pile:${slot.owner}:${slot.pile.zone}`,
              at: norm([centre[0], bottom]),
              size: Math.max(0.02, (bottom - top) / height),
              value: slot.pile.count,
              label: PILE_LABEL[slot.pile.zone] ?? slot.pile.zone,
              mine,
              // 108.2.a. Running a deck out loses the game, so the last few
              // cards are worth a colour.
              low: slot.pile.zone !== 'trash' && slot.pile.count <= 5,
            });
          } else {
            const xp = this.session.state.players[slot.owner]?.xp ?? 0;
            if (xp > 0) {
              items.push({
                kind: 'count',
                key: `xp:${slot.owner}`,
                at: norm([centre[0], bottom]),
                size: Math.max(0.02, (bottom - top) / height),
                value: xp,
                label: 'xp',
                mine,
                low: false,
              });
            }
          }
        }
      }

      // 143. Stats belong on the card, not only in the rail: a buffed unit and
      // an unbuffed one are the same picture, and the number is the difference.
      //
      // Only what is actually on the board. 108.3.d. The Champion Zone is a
      // waiting room, not the table -- a card sitting there has no Might in
      // play to report, and badging it says it is fighting when it is not.
      if (slot.role !== 'unit' && slot.role !== 'gear') continue;
      const centre = this.project([actor.pos[0], actor.pos[1] + actor.lift, actor.pos[2]]);
      if (centre[2] < 0) continue;
      const corners = this.cornersOf(actor);
      if (!corners) continue;
      const top = Math.min(...corners.map((c) => c[1]));
      const bottom = Math.max(...corners.map((c) => c[1]));

      const marks: string[] = [];
      if (slot.exhausted) marks.push('exhausted');
      for (const status of slot.statuses) marks.push(status);

      // Nothing to say is nothing to draw: an empty pill under a gear is
      // furniture, not information.
      if (slot.printedMight === null && slot.damage === 0 && marks.length === 0) continue;

      items.push({
        kind: 'stats',
        id: actor.id,
        at: norm([centre[0], bottom]),
        size: Math.max(0.02, (bottom - top) / height),
        might: slot.might,
        printed: slot.printedMight ?? 0,
        hasMight: slot.printedMight !== null,
        damage: slot.damage,
        marks,
        mine: slot.owner === this.session.human,
      });
    }

    // 164.2. What paying for the card under the pointer would cost.
    //
    // The card being *considered* is the one being carried if there is one,
    // and otherwise whatever is being looked at -- so the preview follows a
    // drag as well as a hover, and disappears the moment neither is true.
    const considering = this.carry?.object ?? this.hovered;
    if (considering !== this.costPreviewFor) {
      this.costPreviewFor = considering;
      this.costPreview = [];
      if (considering !== null && this.affordable.has(considering)) {
        const path = findPaymentPath(this.session.state, this.session.rules, considering);
        for (const step of path ?? []) {
          if (step.t !== 'activate') continue;
          this.costPreview.push({
            id: step.object,
            destructive: tapIsDestructive(this.session.state, this.session.rules, step),
          });
        }
      }
    }
    for (const { id, destructive } of this.costPreview) {
      const actor = this.actors.get(id);
      if (!actor || actor.retiring) continue;
      const corners = this.cornersOf(actor);
      if (!corners) continue;
      shapes.set(`cost:${id}`, {
        hot: false,
        item: {
          kind: 'cost', key: `cost:${id}`, points: corners.map(norm), destructive, appear: 0,
        },
      });
    }

    // 327. Everything waiting on the chain, and what it named.
    //
    // Drawn last so the arrows sit over the cards rather than under them, and
    // built from `state.chain` rather than from anything the client tracks:
    // the targets were chosen at 355 and are on the item, so this is the same
    // reading of the game the engine will resolve from.
    for (const item of this.session.state.chain) {
      const from = this.centreOf(item.source);
      if (!from) continue;
      const mine = item.controller === this.session.human;
      // Several targets from one source fan out rather than stacking: two
      // arrows drawn on the same curve are one arrow as far as the eye is
      // concerned, which is the opposite of the point.
      item.targets.forEach((target, i) => {
        const to = this.centreOf(target);
        if (!to) return;
        const bow = (i - (item.targets.length - 1) / 2) * 26 + (item.targets.length > 1 ? 0 : 18);
        const arrow = this.arrow(from, to, bow);
        if (!arrow) return;
        const key = `aim:${item.id}:${target}`;
        shapes.set(key, {
          hot: false,
          item: { kind: 'aim', key, ...arrow, mine, appear: 0, time: this.time },
        });
      });
    }
    // Arriving and leaving, rather than appearing and vanishing. 14 is quick
    // enough that nothing feels laggy to click and slow enough that the eye
    // reads it as a thing moving into place.
    const RATE = 14;
    const HOT_RATE = 18;
    for (const [key, { item, hot }] of shapes) {
      const held = this.presence.get(key) ?? { v: 0, hot: 0, item };
      held.item = item;
      held.v = damp(held.v, 1, RATE, this.frameDt);
      held.hot = damp(held.hot, hot ? 1 : 0, HOT_RATE, this.frameDt);
      this.presence.set(key, held);
    }
    for (const [key, held] of this.presence) {
      if (shapes.has(key)) continue;
      held.v = damp(held.v, 0, RATE, this.frameDt);
      held.hot = damp(held.hot, 0, HOT_RATE, this.frameDt);
      // Below this it is invisible, and keeping it would leak an entry per
      // target for the length of the match.
      if (held.v < 0.02) this.presence.delete(key);
    }
    for (const held of this.presence.values()) {
      const item = held.item;
      if (item.kind === 'drop') items.push({ ...item, appear: held.v, hot: held.hot });
      else if (item.kind === 'aim' || item.kind === 'cost') {
        items.push({ ...item, appear: held.v });
      }
    }
    return items;
  }

  /** Where a card is on screen, or null if it is not being drawn. */
  private centreOf(id: ObjectId): [number, number] | null {
    const actor = this.actors.get(id);
    if (!actor || actor.retiring) return null;
    const p = this.project([actor.pos[0], actor.pos[1], actor.pos[2]]);
    return p[2] >= 0 ? [p[0], p[1]] : null;
  }

  /**
   * `aimArrow` in normalised coordinates.
   *
   * The shaping is in `aim.ts` and in pixels, because it is geometry that is
   * quietly wrong when it is wrong -- a squashed head or a NaN that makes the
   * whole shape vanish -- and pixels are what it can be tested in.
   */
  private arrow(from: [number, number], to: [number, number], bow: number): Arrow | null {
    const arrow = aimArrow(from, to, bow);
    if (!arrow) return null;
    const norm = (p: [number, number]): [number, number] =>
      [p[0] / this.gpu.width, p[1] / this.gpu.height];
    return { shaft: arrow.shaft.map(norm), head: arrow.head.map(norm) };
  }

  private draw(): void {
    const draws: CardDraw[] = [];
    // Once per frame rather than once per card: the set is the same for all of
    // them, and it is what tells the player where the click is meant to land.
    const offered = this.dropIds();

    for (const actor of this.actors.values()) {
      const slot = actor.slot;
      const card = this.session.rules.index.get(actor.card);

      const pos: Vec3 = [actor.pos[0], actor.pos[1] + actor.lift, actor.pos[2]];
      // A landscape card keeps the same area but swaps which local axis is
      // long. Rotating it instead would turn the artwork with it.
      const wide = slot.landscape ? CARD.height / CARD.width : 1;
      const tall = slot.landscape ? CARD.width / CARD.height : 1;
      compose(
        pos, actor.rot,
        [actor.scale * wide, actor.scale * tall, actor.scale],
        actor.model,
      );

      const domain = card.domains[0] as Domain | undefined;
      const glow = (domain && DOMAIN_COLORS[domain]) ?? ([0.65, 0.68, 0.8] as const);

      // Everything a pending choice will accept pulses -- a battlefield to
      // move to, a unit to aim at -- while merely playable cards get a steady
      // rim.
      const isDropTarget = offered.has(actor.id);
      const tint = actor.highlight * 0.14 + (isDropTarget ? 0.2 : 0);

      draws.push({
        visual: this.library.visualById(actor.card),
        model: actor.model,
        foilTier: BOARD_FOIL ? foilTier(card.rarity) : 0,
        hover: actor.hover,
        bend: slot.role === 'hand' ? -0.004 - actor.hover * 0.004 : -0.0015,
        bendAxis: [1, 0],
        highlight: actor.hover * 0.2,
        reveal: 1,
        seed: actor.seed,
        // A card lying on the table is not being held, so it holds still --
        // except while the pointer is on it, where the flex is the feedback
        // that says the card is live.
        settled: slot.role === 'hand' && this.cardMotion ? 0 : 1 - actor.hover,
        // Damps the foil on a held card; see CardDraw.inHand.
        inHand: slot.role === 'hand' ? 1 : 0,
        // Live, like the camera and the grading: mutate `board.handLight` and
        // the next frame picks it up. Once a value looks right it belongs in
        // HAND_LIGHT, which is where these came from.
        handDirect: slot.role === 'hand' ? this.handLight.direct : 1,
        handAmbient: slot.role === 'hand' ? this.handLight.ambient : 1,
        tint: [glow[0], glow[1], glow[2], tint],
      });
    }

    // The plaque sits on the centre line, tilted up toward the camera so it
    // reads as a sign rather than as something printed on the mat.
    const state = this.session.state;
    this.banner.update({
      turn: state.turn,
      phase: PHASE_NAME[state.phase] ?? state.phase,
      who: state.turnPlayer === this.session.human ? 'Your turn' : 'Opponent',
      yours: state.turnPlayer === this.session.human,
    });
    const plaque = this.banner.draw(BANNER_AT, BANNER_TILT, BANNER_WIDTH);
    if (plaque) draws.push(plaque);

    const scene: Scene = {
      camera: { viewProj: this.viewProj, position: this.cameraPos },
      cards: draws,
      environment: this.env,
      grade: this.grade,
      time: this.time,
      table: tableLayout(this.session.state, this.session.human),
    };
    this.renderer.render(scene);
  }

  // -- picking ---------------------------------------------------------------

  /** Projects a world point into pixel coordinates; w <= 0 means behind the camera. */
  /** A pointer event in the canvas's own device pixels. */
  private canvasPoint(e: PointerEvent, canvas: HTMLCanvasElement): [number, number] {
    const r = canvas.getBoundingClientRect();
    return [
      ((e.clientX - r.left) / r.width) * this.gpu.width,
      ((e.clientY - r.top) / r.height) * this.gpu.height,
    ];
  }

  /**
   * Where on the table a screen point falls.
   *
   * The inverse of `project`, done geometrically rather than by inverting the
   * matrix: build the camera basis, aim a ray through the pixel, and intersect
   * it with the horizontal plane at `height`. That is all a top-down board
   * needs, and it avoids carrying a 4x4 inverse for one caller.
   */
  private pointerOnTable(at: [number, number], height = 0): Vec3 | null {
    const eye = this.cameraPos;
    const fwd = normalize([
      this.cameraTarget[0] - eye[0],
      this.cameraTarget[1] - eye[1],
      this.cameraTarget[2] - eye[2],
    ]);
    const up: Vec3 = [0, 1, 0];
    const right = normalize([
      fwd[1] * up[2] - fwd[2] * up[1],
      fwd[2] * up[0] - fwd[0] * up[2],
      fwd[0] * up[1] - fwd[1] * up[0],
    ]);
    const camUp: Vec3 = [
      right[1] * fwd[2] - right[2] * fwd[1],
      right[2] * fwd[0] - right[0] * fwd[2],
      right[0] * fwd[1] - right[1] * fwd[0],
    ];

    // Pointer is in device pixels, the same space `project` returns.
    const ndcX = (at[0] / this.gpu.width) * 2 - 1;
    const ndcY = 1 - (at[1] / this.gpu.height) * 2;
    const tan = Math.tan(this.camera.fov / 2);
    const dir = normalize([
      fwd[0] + right[0] * ndcX * tan * this.gpu.aspect + camUp[0] * ndcY * tan,
      fwd[1] + right[1] * ndcX * tan * this.gpu.aspect + camUp[1] * ndcY * tan,
      fwd[2] + right[2] * ndcX * tan * this.gpu.aspect + camUp[2] * ndcY * tan,
    ]);

    // Parallel to the table, or pointing away from it: no intersection.
    if (Math.abs(dir[1]) < 1e-5) return null;
    const t = (height - eye[1]) / dir[1];
    if (t <= 0) return null;
    return [eye[0] + dir[0] * t, height, eye[2] + dir[2] * t];
  }

  private project(p: Vec3): [number, number, number] {
    const m = this.viewProj;
    const x = m[0]! * p[0] + m[4]! * p[1] + m[8]! * p[2] + m[12]!;
    const y = m[1]! * p[0] + m[5]! * p[1] + m[9]! * p[2] + m[13]!;
    const w = m[3]! * p[0] + m[7]! * p[1] + m[11]! * p[2] + m[15]!;
    if (w <= 1e-6) return [0, 0, -1];
    return [(x / w * 0.5 + 0.5) * this.gpu.width, (0.5 - y / w * 0.5) * this.gpu.height, w];
  }

  /**
   * Screen-space hit test. Projecting the four corners and testing the quad
   * works for any orientation, which matters because hand cards are tilted and
   * board cards lie flat.
   */
  /**
   * @param interactiveOnly restrict to cards the player may act on.
   *
   * Hovering and clicking want different sets: you can read any face-up card on
   * the table, including the opponent's, but you may only act on your own.
   * Sharing one test meant enemy units could not be inspected at all.
   */
  private pick(
    pointer: [number, number], interactiveOnly: boolean,
    /**
     * A card to look straight through.
     *
     * A dragged card is held at the pointer and lifted clear of the table, so
     * it is always the topmost thing under it -- which makes "what was this
     * released over" unanswerable unless the card doing the releasing is
     * excluded. It is not a hypothetical: with the dragged unit newly eligible
     * as an answer, every drag from base to battlefield resolved to the unit
     * itself and cancelled, and the only way left to move was click-then-click.
     */
    ignore: ObjectId | null = null,
  ): ObjectId | null {
    let best: ObjectId | null = null;
    let bestDepth = Infinity;

    // 144. A battlefield is never `interactive` in the layout -- there is
    // nothing you do to one directly -- but while a choice is pending it is
    // exactly what has to be clicked. Without this the destination click is
    // filtered out before `clicked` ever sees it, and moving a unit to a
    // battlefield silently does nothing.
    const droppable = this.dropIds();

    for (const actor of this.actors.values()) {
      if (actor.retiring || actor.id === ignore) continue;
      // A pile is clickable for *reading*, which is not what `interactive`
      // means -- clicking one starts nothing, it only opens what is in it.
      const pile = actor.slot.role === 'deck' || actor.slot.role === 'trash';
      if (interactiveOnly && !actor.slot.interactive && !droppable.has(actor.id) && !pile) {
        continue;
      }
      // A face-down card must not give itself away by being hovered.
      if (!interactiveOnly && actor.slot.faceDown) continue;

      const corners = this.cornersOf(actor);
      if (!corners) continue;
      if (!pointInQuad(pointer, corners)) continue;

      const centre = this.project([actor.pos[0], actor.pos[1] + actor.lift, actor.pos[2]]);
      if (centre[2] < bestDepth) {
        bestDepth = centre[2];
        best = actor.id;
      }
    }
    return best;
  }

  /**
   * The board objects a pending choice will accept, as ids.
   *
   * `dropTargets` holds locations, and everything that has to answer "may this
   * be clicked?" holds object ids -- so the two are mapped in one place rather
   * than at each site, which is how every battlefield ended up glowing while
   * only one of them was legal.
   */
  private dropIds(): Set<ObjectId> {
    const out = new Set<ObjectId>();
    if (this.pending.kind === 'none') return out;
    for (const id of this.targetChoices.keys()) out.add(id);
    // 144.3. Pressing another unit adds it to the move rather than starting a
    // new gesture, so it has to count as an answer to the one already open --
    // otherwise the rule that "anything the open choice has nothing to say
    // about replaces it" would throw the group away on every second tap.
    if (this.pending.kind === 'moveUnit') {
      for (const id of this.pending.objects) out.add(id);
      for (const id of this.moveJoinable) out.add(id);
    }
    for (const t of this.dropTargets) {
      if (t.location.kind !== 'battlefield') continue;
      const bf = this.session.state.battlefields[t.location.index];
      if (bf) out.add(bf.object);
    }
    return out;
  }

  /**
   * Places a pending choice will accept that have no card to click.
   *
   * A battlefield is a card and can be picked like one; a base is a printed
   * rectangle on the mat with nothing standing on it, so it gets a footprint
   * of its own. Both come from the same `dropTargets` list, so neither can
   * offer something the engine will refuse.
   */
  private dropSpots(): { action: Action; corners: [number, number][]; label: string }[] {
    if (this.pending.kind === 'none') return [];
    const out: { action: Action; corners: [number, number][]; label: string }[] = [];
    for (const t of this.dropTargets) {
      if (t.location.kind !== 'base') continue;
      const mine = t.location.player === this.session.human;
      const zone = zoneFor('base', mine);
      const corners = this.quadOnTable(zone.x, zone.z, zone.halfW, zone.halfD);
      if (corners) out.push({ action: t.action, corners, label: mine ? 'Your base' : 'Their base' });
    }
    return out;
  }

  /** A flat rectangle on the mat, projected to screen space. */
  private quadOnTable(
    x: number, z: number, halfW: number, halfD: number,
  ): [number, number][] | null {
    const out: [number, number][] = [];
    for (const [dx, dz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
      const p = this.project([x + dx * halfW, 0.004, z + dz * halfD]);
      if (p[2] < 0) return null;
      out.push([p[0], p[1]]);
    }
    return out;
  }

  private cornersOf(actor: Actor): [number, number][] | null {
    const pos: Vec3 = [actor.pos[0], actor.pos[1] + actor.lift, actor.pos[2]];
    // The same swap the draw uses, or a landscape battlefield would be picked
    // through a portrait footprint it no longer occupies.
    const wide = actor.slot.landscape ? CARD.height / CARD.width : 1;
    const tall = actor.slot.landscape ? CARD.width / CARD.height : 1;
    compose(
      pos, actor.rot,
      [actor.scale * wide, actor.scale * tall, actor.scale],
      actor.model,
    );
    const m = actor.model;

    const out: [number, number][] = [];
    for (const [lx, ly] of [[-HALF_W, -HALF_H], [HALF_W, -HALF_H], [HALF_W, HALF_H], [-HALF_W, HALF_H]] as const) {
      const world: Vec3 = [
        m[0]! * lx + m[4]! * ly + m[12]!,
        m[1]! * lx + m[5]! * ly + m[13]!,
        m[2]! * lx + m[6]! * ly + m[14]!,
      ];
      const p = this.project(world);
      if (p[2] < 0) return null;
      out.push([p[0], p[1]]);
    }
    return out;
  }

  // -- input -----------------------------------------------------------------

  private attachInput(): void {
    const canvas = this.gpu.canvas;
    const on = <K extends keyof HTMLElementEventMap>(
      target: HTMLElement | Window, type: K,
      fn: (e: HTMLElementEventMap[K]) => void, opts?: AddEventListenerOptions,
    ) => {
      target.addEventListener(type, fn as EventListener, opts);
      this.detach.push(() => target.removeEventListener(type, fn as EventListener, opts));
    };

    // A wheel over the table scrolls whatever the panel is showing. Not
    // passive, because a scroll the panel consumed must not also scroll the
    // page behind it.
    on(canvas, 'wheel', (e) => {
      // `deltaMode` is lines on Firefox and pages on some setups; the panel
      // wants pixels, and a line is about one line of the panel's own text.
      // Firefox reports lines and some setups report pages; the panel works
      // in pixels, so both are converted before anything is scaled.
      const scale = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1;
      if (this.callbacks.onScroll(e.deltaY * scale)) e.preventDefault();
    }, { passive: false });

    on(canvas, 'pointermove', (e) => {
      this.pointer = this.canvasPoint(e, canvas);
      // A few pixels of travel is a shaky click, not a drag. Measured in
      // device pixels, which is what `pointer` is in.
      if (this.press && !this.press.moved) {
        const dx = this.pointer[0] - this.press.at[0];
        const dy = this.pointer[1] - this.press.at[1];
        if (dx * dx + dy * dy > 64) this.press.moved = true;
      }
      // Past the threshold the card leaves its slot and follows the pointer.
      // Only the player's own cards: dragging an opponent's unit would look
      // like it could be moved, and it cannot.
      if (this.press?.moved && this.press.id !== null) {
        const actor = this.actors.get(this.press.id);
        if (actor && actor.slot.owner === this.session.human) {
          const at = this.pointerOnTable(this.pointer);
          if (at) this.dragging = { id: this.press.id, at };
        }
      }
    });

    // Pressing a card begins what clicking it begins. Releasing over a target
    // finishes it, and releasing where it started leaves the choice open --
    // which is click-then-click, so both gestures come out of one path and
    // cannot disagree about what is legal.
    on(canvas, 'pointerdown', (e) => {
      if (e.button === 2) { this.cancel(); return; }
      if (e.button !== 0) return;
      const at = this.canvasPoint(e, canvas);
      this.pointer = at;
      // 811. Hiding is the second thing a card in hand can do, and it needs a
      // second gesture to ask for it: shift-press hides instead of playing.
      this.hideModifier = e.shiftKey;
      // An unknown `pointerType` is treated as a mouse: the two-tap rule is
      // the unusual path and should not be entered on a guess.
      this.touchMode = e.pointerType === 'touch' || e.pointerType === 'pen';
      if (!this.touchMode) this.picked = null;

      // A press may only *offer*. It may not play a card, use an ability, tap
      // a rune or answer a choice -- every one of those waits for the release.
      //
      // This is the whole rule, and it is not a preference. Pressing used to
      // run `clicked`, which submits: pressing a Seal of Insight activated it,
      // so a player who meant to drag the card across their base triggered it
      // instead, and `payForAbility` had already spent runes to make that
      // happen. There is no undo for either. A press that commits also leaves
      // the drop with nothing left to decide, which is what made dragging a
      // click with extra steps.
      const hit = this.pick(at, true);

      // A press on something the open choice has nothing to say about
      // *replaces* it, rather than being folded into it.
      //
      // On a touchscreen the first tap on a card is a look, and a look leaves
      // a choice open behind it. Pressing a *different* card afterwards was
      // then read as answering the old choice: the drag carried the new card,
      // and the drop played the old one. Whatever is under the finger is what
      // the gesture is about, so anything that is not an answer to the choice
      // already running ends it and starts its own.
      //
      // Safe to end here because `pending` is a gesture this client started,
      // not a question the engine is waiting on -- those live in
      // `state.pendingChoice` and are answered from the choice panel.
      const answers = this.pending.kind !== 'none'
        && (
          (hit !== null && this.dropIds().has(hit))
          || this.dropSpots().some((d) => pointInQuad(at, d.corners))
        );
      if (this.pending.kind !== 'none' && hit !== null && !answers) this.cancel();

      const hadPending = this.pending.kind !== 'none';
      this.press = { id: hit, at, moved: false, opened: false, hadPending };

      if (!hadPending) {
        if (hit === null) {
          this.cancel();
        } else if (this.carrying(hit)) {
          // A card in hand: light up where it could go, spend nothing.
          this.offerPlay(hit);
          this.press.opened = this.pending.kind !== 'none' || this.carry !== null;
        } else {
          // A unit of yours that can move: light up where to. Its abilities are
          // not touched -- a click asks about those.
          const moves = this.movesFor(hit);
          if (moves.length > 0) { this.beginMove(hit, moves); this.press.opened = true; }
        }
      }

      this.hideModifier = false;
      canvas.setPointerCapture(e.pointerId);
    });

    on(canvas, 'pointerup', (e) => {
      const press = this.press;
      this.press = null;
      if (press === null || e.button !== 0) return;
      const at = this.canvasPoint(e, canvas);
      this.pointer = at;
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);

      // Released without travelling: that was a click, and a click is what
      // commits. Everything that changes the game happens from here or from a
      // drop -- never from the press that started either.
      if (!press.moved) {
        this.clickedAt(press, at);
        return;
      }

      // A drag: whatever it was released over answers the pending choice.
      const dropped = this.dragging;
      this.dragging = null;

      if (this.pending.kind !== 'none') {
        const spot = this.dropSpots().find((d) => pointInQuad(at, d.corners));
        if (spot) { this.submit(spot.action); return; }
        // Looking past the card in hand: it is the thing being dropped, not
        // the thing being dropped on.
        const over = this.pick(at, true, dropped?.id ?? null);
        if (over !== null && this.dropIds().has(over)) { this.clicked(over); return; }
      }

      // A card carried out of the hand and dropped anywhere that will not take
      // it goes back, unplayed and unpaid for. Dragging it home is how you
      // change your mind, so that has to be the harmless outcome rather than
      // the one that casts the spell.
      // Dropped back among its own kind: the player is sorting their hand.
      //
      // Checked before the carry and independently of it, because sorting has
      // nothing to do with playing: a card you cannot afford never becomes a
      // carry at all, and hanging the reorder off one meant a hand of
      // unaffordable cards -- which is most opening hands -- could not be
      // sorted. Neither gesture touches the game.
      const droppedSlot = dropped ? this.actors.get(dropped.id)?.slot : undefined;
      if (droppedSlot?.role === 'hand' && droppedSlot.owner === this.session.human
        && this.overOwnHand(at)) {
        const object = dropped!.id;
        this.cancel();
        const moved = this.reorderHand(object, at);
        this.callbacks.onPrompt(moved
          ? 'Hand reordered.'
          : 'Back in your hand. Nothing was played and no runes were spent.');
        return;
      }

      if (this.carry) {
        // A spell names no destination -- there is nothing on the mat to drop
        // it on -- so anywhere but the hand it came from means "play it".
        const loose = this.carriedDefault(true);
        if (loose) { this.submit(loose); return; }
        this.cancel();
        this.callbacks.onPrompt(
          'Nothing there will take that card. Drop it on a highlighted place.');
        return;
      }

      // Released somewhere that answers nothing yet.
      //
      // Arranging is confined to the zone the card is already in: a rune is
      // rearranged among the runes, a unit among the cards in its base. A drop
      // anywhere else is a *move* being asked for, and it has to be put to the
      // engine -- because the alternative is what shipped, where dropping a
      // unit on a battlefield quietly parked it there. It looked exactly like a
      // conquest: the card stood on the battlefield, and the engine had never
      // been told anything, so nothing was contested, no showdown opened and
      // no point was scored.
      if (dropped) this.dropped(dropped.id, at);
    });

    // A pointer that leaves the window without releasing must not leave a
    // half-finished drag behind.
    on(canvas, 'pointercancel', () => { this.press = null; this.dragging = null; });

    on(canvas, 'contextmenu', (e) => e.preventDefault());

    on(window, 'keydown', (e) => {
      if (e.key === 'Escape') this.cancel();
      if (e.key === ' ') { e.preventDefault(); this.endTurn(); }
    });
  }

  private clicked(id: ObjectId): void {
    this.closeInspect();
    if (!this.session.waitingOnHuman) return;

    // 117.1. During the mulligan a click marks a card for replacement rather
    // than playing it. Clicking a marked card unmarks it.
    if (this.session.state.waiting.kind === 'mulligan') {
      const card = this.session.state.objects[id];
      if (!card || card.zone.kind !== 'hand') return;
      if (this.mulliganPicks.has(id)) this.mulliganPicks.delete(id);
      else if (this.mulliganPicks.size < 2) this.mulliganPicks.add(id);
      // The pick is not game state, so nothing else would tell the HUD to
      // relabel its button from "Keep hand" to "Replace 2".
      this.callbacks.onState();
      this.callbacks.onPrompt(this.prompt());
      return;
    }

    const actions = this.session.humanActions();

    // Resolving a pending choice: did they click one of the offered targets?
    if (this.pending.kind !== 'none') {
      const aimed = this.targetChoices.get(id);
      if (aimed) { this.submit(aimed); return; }
      const slot = this.actors.get(id)?.slot;
      if (slot?.role === 'battlefield') {
        const bfIndex = this.session.state.battlefields
          .findIndex((b) => b.object === id);
        const target = this.dropTargets.find((t) =>
          t.location.kind === 'battlefield' && t.location.index === bfIndex);
        if (target) { this.submit(target.action); return; }
      }
      // 144.3. Another of your ready units joins the move; one already in it
      // drops out. The last one cannot drop out -- that is just cancelling,
      // which is what falls through below.
      if (this.pending.kind === 'moveUnit') {
        const group = this.pending;
        if (group.objects.includes(id)) {
          if (group.objects.length > 1) {
            group.objects = group.objects.filter((o) => o !== id);
            this.refreshMove();
            this.callbacks.onState();
            return;
          }
        } else if (this.moveJoinable.has(id)) {
          group.objects = [...group.objects, id];
          this.refreshMove();
          this.callbacks.onState();
          return;
        }
      }
      this.cancel();
      return;
    }

    const slot = this.actors.get(id)?.slot;
    if (!slot) return;

    // 108. Reading a pile. A trash is public and opens in full; a deck is not,
    // so it reports its size and nothing else -- showing it would hand the
    // player information the rules do not give them.
    if (slot.role === 'trash' || slot.role === 'deck') {
      this.inspectPile(slot);
      return;
    }

    // 164.2. Runes are not tapped by hand.
    //
    // Exhausting a rune and recycling one both "add resources", but a recycle
    // sends it to the Rune Deck for the rest of the game. Tapping by hand made
    // that a click away from being done by accident, and resources float only
    // until the pool empties (167.1), so a mistaken tap is a rune spent on
    // nothing. Every cost is paid by the payment search instead, which is asked
    // for a specific card or ability and prefers an exhaust to a recycle.
    if (slot.role === 'rune' && slot.owner === this.session.human) {
      if (!this.manualRunes) {
        const object = this.session.state.objects[id];
        this.callbacks.onPrompt(object?.exhausted
          ? 'That rune is exhausted. It readies at the start of your turn.'
          : 'Runes are spent for you -- click the card or ability you want to pay for.');
        return;
      }
      const taps = actions.filter((a): a is Extract<Action, { t: 'activate' }> =>
        a.t === 'activate' && a.object === id);
      if (taps.length > 1 && this.offerTargets(taps, 'use it on')) return;
      const tap = taps[0];
      if (tap) this.submit(tap);
      else this.callbacks.onPrompt(this.session.state.objects[id]?.exhausted
        ? 'That rune is already exhausted.'
        : 'You cannot tap a rune right now.');
      return;
    }

    if (slot.role === 'hand' || slot.role === 'champion') {
      // 811. A card with [Hidden] can go face down at a battlefield you
      // control instead of being played, for its hide cost.
      const hides = actions.filter((a): a is Extract<Action, { t: 'hide' }> =>
        a.t === 'hide' && a.object === id);
      if (this.hideModifier && hides.length > 0) {
        if (hides.length === 1) { this.submit(hides[0]!); return; }
        this.pending = { kind: 'hideCard', object: id, options: hides };
        this.dropTargets = hides.map((a) => ({
          location: { kind: 'battlefield', index: a.battlefield }, action: a,
        }));
        this.callbacks.onPrompt('Choose where to hide it.');
        return;
      }

      let plays = actions.filter((a) => a.t === 'play' && a.object === id);

      // Nothing playable yet usually means the resources are still sitting in
      // untapped runes. Riftbound makes you activate them one at a time, which
      // is correct but tedious, so find a sequence of activations that pays for
      // the card and run it. The search happens on a clone, so a card that
      // cannot be afforded never taps anything.
      if (plays.length === 0) {
        const path = this.findPaymentPath(id);
        if (!path) {
          this.callbacks.onPrompt(this.whyNotPlayable(id, slot.card));
          return;
        }
        for (const step of path) this.session.submit(step);
        plays = this.session.humanActions().filter((a) => a.t === 'play' && a.object === id);
        if (plays.length === 0) return;
      }

      if (this.offerTargets(plays, 'play it on')) return;

      if (plays.length === 1) {
        if (hides.length > 0) {
          this.callbacks.onPrompt('Played. Shift-click a [Hidden] card to hide it instead.');
        }
        this.submit(plays[0]!);
        return;
      }
      // Several destinations: ask, by lighting up the valid ones.
      this.pending = { kind: 'placeUnit', object: id, options: plays };
      this.dropTargets = plays
        .filter((a): a is Extract<Action, { t: 'play' }> => a.t === 'play' && !!a.to)
        .map((a) => ({ location: a.to!, action: a }));
      // Playing to base needs no click; offer it as the default on a second
      // click of the same card.
      this.callbacks.onPrompt('Choose where to play it, or click the card again for your base.');
      return;
    }

    if (slot.role === 'unit' && slot.owner === this.session.human) {
      const moves = actions.filter((a): a is Extract<Action, { t: 'move' }> =>
        a.t === 'move' && a.objects.includes(id));
      const uses = this.activationsOn(id, actions);

      // A unit can both move and have an ability to use, and a single click
      // cannot mean both -- so when both are on offer the card asks which.
      if (moves.length > 0 && uses.length > 0) {
        this.openMenu(id, [
          { label: 'Move', sub: 'Choose where to move', action: null },
          ...this.abilityItems(id, uses),
        ]);
        return;
      }
      if (moves.length === 0) {
        if (uses.length > 0) { this.useAbility(id, uses); return; }
        // 462. Silence is indistinguishable from a broken client, and moving is
        // the gesture that conquers a battlefield -- so say which of the three
        // reasons it is rather than letting the click vanish.
        const unit = this.session.state.objects[id];
        const def = unit && this.session.rules.index.get(unit.card);
        this.callbacks.onPrompt(
          unit?.exhausted
            ? `${def ? def.name : 'That unit'} is exhausted and cannot move.`
            : `No legal move for ${def ? def.name : 'that unit'} right now.`,
        );
        return;
      }
      this.beginMove(id, moves);
      return;
    }

    // 818. Everything else that stands on the table -- Equipment, other gear,
    // the legend, a battlefield -- is clicked for the ability printed on it.
    // Only a rune's activation used to be reachable, so Equip, "exhaust me to
    // ready up to 2 runes" and every battlefield ability were unusable.
    if (slot.owner === this.session.human) {
      this.useAbility(id, this.activationsOn(id, actions));
      return;
    }
  }

  /**
   * What a click means, once the release has confirmed it was one.
   *
   * Three cases, and they are different gestures wearing the same button:
   * finishing a choice that was already open, taking the default of an offer
   * this press just made, and acting on a card that offered nothing.
   */
  private clickedAt(
    press: { id: ObjectId | null; hadPending: boolean; opened: boolean }, at: [number, number],
  ): void {
    // Finishing a choice that was already on screen: a place, then a card.
    // This comes first on every input, because a tap on a highlighted
    // destination is unambiguous -- it is the answer to a question already
    // being asked, and making the player tap it twice would be nonsense.
    if (press.hadPending) {
      const spot = this.dropSpots().find((d) => pointInQuad(at, d.corners));
      if (spot) { this.picked = null; this.submit(spot.action); return; }
      if (press.id !== null && this.dropIds().has(press.id)) {
        this.picked = null;
        this.clicked(press.id);
        return;
      }
    }

    // On a touchscreen the first tap on a card is a *look*.
    //
    // There is no hover to read a card with, so a tap that commits leaves no
    // way to see what a card does before doing it -- which is what "every tap
    // is instant playing that card" meant. The press has already lit up
    // wherever the card could go and spent nothing, so this just declines to
    // finish the gesture and lets the panel show the card.
    if (this.touchMode) {
      // 117.1. The mulligan is the exception: a tap marks a card for
      // replacement and a second tap unmarks it. Nothing is spent, nothing is
      // played, and it is undone by tapping again -- so making the player tap
      // twice to mark one card is friction with nothing on the other side.
      if (this.session.state.waiting.kind === 'mulligan') {
        if (press.id !== null) {
          // ...and the tap still has to *show* the card. `hovered` is
          // `picked` on a touchscreen, so returning without setting it left
          // the panel empty -- which is being asked to choose which of two
          // cards to throw away while able to read neither.
          this.picked = press.id;
          this.clicked(press.id);
        }
        return;
      }

      const looked = press.id ?? this.pick(at, false);
      if (looked === null) { this.picked = null; this.cancel(); return; }
      if (this.picked !== looked) { this.picked = looked; return; }

      // The second tap on the same card does *not* play it.
      //
      // Two taps in the same place is what a finger does by accident -- a
      // slightly long press, a bounce, a tap while the board is still moving --
      // and playing a card is not undoable. So a card is played by dragging it
      // where it goes, or by tapping a destination that has lit up: both name
      // the destination as well as the card, and neither can happen twice by
      // mistake. Everything with no drag to make -- an ability, a pile -- still
      // answers to a tap, because there is nothing else it could answer to.
      this.picked = null;
      if (this.pending.kind === 'moveUnit'
        && this.pending.objects.length === 1 && this.pending.objects[0] === looked) {
        const uses = this.activationsOn(looked, this.session.humanActions());
        if (uses.length > 0) {
          this.cancel();
          this.openMenu(looked, [
            { label: 'Move', sub: 'Choose where to move', action: null },
            ...this.abilityItems(looked, uses),
          ]);
        }
        // Otherwise its destinations are lit and the next tap answers them.
        return;
      }
      if (press.id !== null && !press.hadPending) this.clicked(press.id);
      return;
    }

    if (press.hadPending) {
      if (press.id !== null) this.clicked(press.id);
      else this.cancel();
      return;
    }

    // An offer this press made. A card with one destination goes there -- a
    // click should still play a card in one gesture -- and a unit that can do
    // more than move stops to ask which, because one click cannot mean both.
    if (press.opened) {
      const only = this.carriedDefault();
      if (only) { this.submit(only); return; }
      if (press.id !== null && this.pending.kind === 'moveUnit') {
        const uses = this.activationsOn(press.id, this.session.humanActions());
        if (uses.length > 0) {
          this.cancel();
          this.openMenu(press.id, [
            { label: 'Move', sub: 'Choose where to move', action: null },
            ...this.abilityItems(press.id, uses),
          ]);
        }
      }
      return;   // otherwise the offer stays up for a second click to answer
    }

    // A card that offered nothing on the press: its ability, its pile, its
    // explanation of why it can do none of that.
    if (press.id !== null) this.clicked(press.id);
  }

  /** The moves on offer for one of the player's own units. */
  /** Whether this unit is one of those a composed move is carrying. */
  private inPendingMove(id: ObjectId): boolean {
    return this.pending.kind === 'moveUnit' && this.pending.objects.includes(id);
  }

  /** Every single-unit and group move the engine is currently offering. */
  private moveActions(): Move[] {
    return this.session.humanActions().filter((a): a is Move => a.t === 'move');
  }

  /**
   * The other ready units that could join the move being composed.
   *
   * Recomputed when the group changes rather than read per frame: it asks the
   * engine for every legal action, and `dropIds` is consulted three or four
   * times a frame by the overlay and the hit test. Nothing else can change it
   * while a move is being composed -- no action has been submitted yet.
   */
  private refreshJoinable(): void {
    if (this.pending.kind !== 'moveUnit') { this.moveJoinable = new Set(); return; }
    const mine: ObjectId[] = [];
    for (const [id, actor] of this.actors) {
      if (actor.slot.role === 'unit' && actor.slot.owner === this.session.human) mine.push(id);
    }
    this.moveJoinable = joinable(this.moveActions(), this.pending.objects, mine);
  }

  /**
   * Re-lights the destinations for the group as it now stands.
   *
   * Called after every change to the group rather than once at the start,
   * because adding a unit narrows what the group can share.
   */
  private refreshMove(): void {
    if (this.pending.kind !== 'moveUnit') return;
    this.refreshJoinable();
    const objects = [...this.pending.objects];
    const shared = sharedDestinations(this.moveActions(), objects);
    this.dropTargets = [...shared.values()].map((location) => ({
      location, action: groupMove(objects, location),
    }));
    this.callbacks.onPrompt(objects.length > 1
      ? `Moving ${objects.length} units together -- choose where.`
      : 'Choose where to move, or tap another unit to send it too.');
  }

  private movesFor(id: ObjectId): Extract<Action, { t: 'move' }>[] {
    if (!this.session.waitingOnHuman) return [];
    if (this.session.state.waiting.kind === 'mulligan') return [];
    const slot = this.actors.get(id)?.slot;
    if (slot?.role !== 'unit' || slot.owner !== this.session.human) return [];
    return this.session.humanActions().filter(
      (a): a is Extract<Action, { t: 'move' }> => a.t === 'move' && a.objects.includes(id));
  }

  // -- carrying a card out of the hand ---------------------------------------

  /** Whether pressing this card should offer a play rather than make one. */
  private carrying(id: ObjectId): boolean {
    if (this.pending.kind !== 'none' || this.hideModifier) return false;
    if (this.session.state.waiting.kind === 'mulligan') return false;
    if (!this.session.waitingOnHuman) return false;
    const slot = this.actors.get(id)?.slot;
    if (!slot || slot.owner !== this.session.human) return false;
    // 108.3.d. A champion is *played* from its zone, exactly like a card in
    // hand -- `clicked` has always treated the two together. Leaving the
    // champion out here meant a press on it opened no offer, so the release
    // fell through to the move path and refused it with "cannot move there",
    // while clicking the same card played it. One gesture said no and the
    // other said yes about the same legal play.
    return slot.role === 'hand' || slot.role === 'champion';
  }

  /**
   * Lights up everywhere a card in hand could go, and commits to none of it.
   *
   * The payment is worked out here but held: `carry.path` is run at the drop,
   * so a card picked up and put back costs nothing. Doing it the other way --
   * paying on the press -- was how a cancelled drag left runes exhausted for a
   * play that never happened, and 167.1 then threw the resource away.
   */
  private offerPlay(id: ObjectId): void {
    this.closeInspect();
    const actions = this.session.humanActions();
    let plays = actions.filter((a) => a.t === 'play' && a.object === id);
    let path: Action[] = [];

    if (plays.length === 0) {
      const found = this.findPaymentPath(id);
      if (!found) {
        this.callbacks.onPrompt(
          this.whyNotPlayable(id, this.actors.get(id)!.slot.card));
        return;
      }
      path = found;
      plays = playsAfterPaying(this.session.state, this.session.rules, id, path);
      if (plays.length === 0) return;
    }

    this.carry = { object: id, path };
    // A spell that can be aimed several ways asks which, exactly as before.
    if (this.offerTargets(plays, 'play it on')) return;

    this.pending = { kind: 'placeUnit', object: id, options: plays };
    this.dropTargets = plays
      .filter((a): a is Extract<Action, { t: 'play' }> => a.t === 'play' && !!a.to)
      .map((a) => ({ location: a.to!, action: a }));
    this.callbacks.onPrompt(this.dropTargets.length > 0
      ? 'Drag it where you want it, or click a highlighted place.'
      : 'Drop it on the table to play it, or back in your hand to keep it.');
  }

  /**
   * The play a carried card makes when it is simply let go.
   *
   * `loose` widens it to a card with no destination on the mat -- a spell has
   * nowhere to be dropped *on*, so releasing it over the table is the whole of
   * the gesture. A card with real destinations is never resolved this way:
   * picking one of several places is what the drop is for.
   */
  private carriedDefault(loose = false): Action | null {
    const carry = this.carry;
    if (!carry || this.pending.kind !== 'placeUnit') return null;
    const options = this.pending.options;
    if (options.length !== 1) return null;
    const only = options[0]!;
    if (loose && only.t === 'play' && only.to) return null;
    return only;
  }

  /**
   * Moves a card to where it was dropped among the rest of the hand.
   *
   * The insert point comes from the card's x against the cards actually on
   * screen, not from an index: the hand is fanned and overlapping, so what the
   * player aimed at is a gap between two cards rather than a slot number.
   *
   * Returns whether the order changed, so the prompt can say which of the two
   * things this gesture just did.
   */
  private reorderHand(object: ObjectId, at: [number, number]): boolean {
    const world = this.pointerOnTable(at);
    if (!world) return false;

    // The hand as it is laid out right now, left to right. Reading it off the
    // slots rather than off the state is what keeps this honest: it reorders
    // what the player can see, including any order they already set.
    const hand = [...this.actors.values()]
      .filter((a) => a.slot.role === 'hand' && a.slot.owner === this.session.human
        && !a.retiring)
      .sort((a, b) => a.slot.position[0] - b.slot.position[0])
      .map((a) => a.id);
    if (hand.length < 2) return false;

    const from = hand.indexOf(object);
    if (from < 0) return false;

    // How many cards the drop point is past. Compared against each card's own
    // x so an uneven fan -- which is every fan, since the spread depends on
    // how many cards are held -- still splits where it looks like it should.
    let to = 0;
    for (const id of hand) {
      if (id === object) continue;
      const x = this.actors.get(id)?.slot.position[0] ?? 0;
      if (world[0] > x) to++;
    }

    const next = hand.filter((id) => id !== object);
    next.splice(Math.max(0, Math.min(next.length, to)), 0, object);
    if (next.every((id, i) => id === hand[i])) return false;

    // Anything already in the stored order that is not in hand any more keeps
    // its place, behind the new arrangement, so a card that comes back to hand
    // returns to where its owner had put it.
    const held = new Set(next);
    this.handOrder = [...next, ...this.handOrder.filter((id) => !held.has(id))];

    // Re-lay the board now, rather than waiting for the game to change.
    //
    // `sync` is what reads `handOrder`, and it normally runs off the match's
    // own change callback -- but nothing about the game changed here, which is
    // the entire point. Telling only the HUD left the new order sitting unused
    // until the next action was played, so the hand appeared to reorder itself
    // one move late.
    //
    // Nothing is sent anywhere. A hand is secret, so there is no second view of
    // it to keep in step: the opponent sees a fixed row of card backs whatever
    // order they are in, and telling them it changed would leak that something
    // happened while giving them nothing they are entitled to know.
    this.sync();
    return true;
  }

  /** Whether a release landed back among the player's own hand. */
  private overOwnHand(at: [number, number]): boolean {
    const over = this.pick(at, true);
    if (over !== null && this.actors.get(over)?.slot.role === 'hand') return true;
    const world = this.pointerOnTable(at);
    // Past the runes strip is the near edge of the table, where the hand is
    // held. A card released out there was on its way home.
    return world !== null && world[2] > HAND_RETURN_Z;
  }

  /**
   * What a drag that answered no choice was asking for.
   *
   * Inside the card's own zone it is an arrangement; anywhere else it is a
   * move, and the engine decides whether that move exists.
   */
  private dropped(id: ObjectId, at: [number, number]): void {
    const where = this.arrangeZoneOf(id);
    const world = this.pointerOnTable(at);
    if (!world) return;

    if (where && insideZone(world[0], world[2], where, true)) {
      this.arrangeAt(id, where, world);
      return;
    }

    // Outside its own zone. Ask the engine for a move to whatever place that
    // is; anything it does not offer is refused out loud, never silently.
    const to = this.placeAt(world);
    if (to) {
      // 144.3. A group that has been composed travels as a group. The
      // destinations it lit up already carry the whole set, and dragging one
      // of its members is not a request to send that one alone -- which is
      // exactly what this did, so a player who selected three units and
      // dragged saw one of them walk in and get into a fight by itself.
      const target = this.dropTargets.find((t) => sameLocation(t.location, to));
      if (target) { this.submit(target.action); return; }

      // Nothing pending: the single-unit move, explicitly. `objects.includes`
      // would also match the whole-army action the engine offers, which is not
      // what dragging one card asked for either.
      const move = this.session.humanActions().find(
        (a): a is Extract<Action, { t: 'move' }> =>
          a.t === 'move' && a.objects.length === 1 && a.objects[0] === id
          && sameLocation(a.to, to));
      if (move) { this.submit(move); return; }
    }
    this.refuseMove(id, to);
  }

  /**
   * Why a card cannot be played, told honestly.
   *
   * The payment search returning nothing was reported as "not enough
   * resources" whatever the reason, and over six real games *every* one of the
   * 964 refusals was a card that was not playable at all -- not one was a
   * payment failure. A player looking at six ready runes of the right domain
   * is being told something they can see is false, and the real reason (no
   * legal target, or a window this card cannot be played in) goes unsaid.
   *
   * Two probes on clones, only on this path: a click that was refused is rare
   * and the player is waiting for an answer either way.
   */
  private whyNotPlayable(id: ObjectId, cardId: string): string {
    const def = this.session.rules.index.get(cardId);
    const price = `${def.cost.energy} energy`
      + (def.cost.power > 0 ? `, ${def.cost.power} power` : '');

    // 1. Would it be legal if the resources were simply there?
    const rich = structuredClone(this.session.state);
    const pool = rich.players[this.session.human]?.pool;
    if (pool) {
      pool.energy += 99;
      pool.any += 99;
      for (const d of DOMAINS) pool.power[d] += 99;
    }
    const affordable = legalActions(rich, this.session.rules)
      .some((a) => a.t === 'play' && a.object === id);
    if (affordable) return `Not enough resources for ${def.name} (${price}).`;

    // 2. It is not the money. Is it the moment, or is there nothing to aim at?
    // Asked by putting the same card in a quiet main phase of the player's own
    // turn, which is the most permissive window there is.
    const anytime = structuredClone(rich);
    anytime.turnPlayer = this.session.human;
    anytime.phase = 'main';
    anytime.showdownState = 'neutral';
    anytime.openState = 'open';
    anytime.chain = [];
    anytime.waiting = { kind: 'priority', player: this.session.human };
    const wouldBeLater = legalActions(anytime, this.session.rules)
      .some((a) => a.t === 'play' && a.object === id);

    return wouldBeLater
      ? `${def.name} cannot be played right now -- it is not a legal moment for it.`
      : `${def.name} has nothing it can legally target right now.`;
  }

  /** Which zone a card of the player's is arranged within, if any. */
  private arrangeZoneOf(id: ObjectId): ArrangeZone | null {
    const slot = this.actors.get(id)?.slot;
    if (!slot || slot.owner !== this.session.human) return null;
    // A rune's *location* is its owner's base, but it is drawn in the runes
    // strip -- so this follows where the card is drawn, not where the rules
    // say it lives. Reading the engine's zone instead threw rearranged runes
    // into the middle of the base.
    if (slot.role === 'rune') return 'runes';
    return this.restingInMyBase(id) ? 'base' : null;
  }

  /** The place a world point falls on: a battlefield, or somebody's base. */
  private placeAt(world: Vec3): Location | null {
    const state = this.session.state;
    for (let i = 0; i < state.battlefields.length; i++) {
      const at = battlefieldAnchor(state, i);
      if (Math.abs(world[0] - at[0]) <= BATTLEFIELD_ZONE.halfW
        && Math.abs(world[2] - at[2]) <= BATTLEFIELD_ZONE.halfD) {
        return { kind: 'battlefield', index: i };
      }
    }
    for (const player of [this.session.human, this.session.human === 0 ? 1 : 0] as const) {
      const zone = zoneFor('base', player === this.session.human);
      if (Math.abs(world[0] - zone.x) <= zone.halfW
        && Math.abs(world[2] - zone.z) <= zone.halfD) {
        return { kind: 'base', player };
      }
    }
    return null;
  }

  /**
   * Says why a drag went nowhere.
   *
   * A drop that produces no visible result and no message is indistinguishable
   * from a broken client -- and here it was worse than that, because the card
   * stayed where it was dropped and looked like it had moved.
   */
  private refuseMove(id: ObjectId, to: Location | null): void {
    const object = this.session.state.objects[id];
    const def = object && this.session.rules.index.get(object.card);
    const name = def ? def.name : 'That card';
    if (!this.session.waitingOnHuman) {
      this.callbacks.onPrompt(`It is not your turn -- ${name} stays where it is.`);
      return;
    }
    if (!to) {
      this.callbacks.onPrompt(`There is nothing there to move ${name} to.`);
      return;
    }
    if (object?.exhausted) {
      this.callbacks.onPrompt(`${name} is exhausted and cannot move.`);
      return;
    }
    this.callbacks.onPrompt(`${name} cannot move there right now.`);
  }

  /**
   * Puts a card on the nearest free cell of its zone's grid.
   *
   * Snapped rather than left where it was dropped: free placement read as
   * drift, and two players looking at the same table have to agree about where
   * a card is. The cell goes to the match, which is what carries it to anyone
   * watching.
   */
  private arrangeAt(id: ObjectId, where: ArrangeZone, world: Vec3): void {
    const wanted = cellAt(world[0], world[2], where, true);
    this.session.arrange(id, this.freeCell(wanted, id, where));
    this.callbacks.onPrompt(this.prompt());
  }

  /**
   * The wanted cell, or the nearest one no other card has been put on.
   *
   * Without this two cards can be arranged onto the same square and the one
   * underneath is simply gone from view -- which looks exactly like a card
   * that left the board.
   */
  private freeCell(
    wanted: [number, number], self: ObjectId, where: ArrangeZone,
  ): [number, number] {
    const grid = ARRANGE_GRID[where];
    const taken = new Set<string>();
    for (const [key, cell] of Object.entries(this.session.arrangement)) {
      const id = Number(key);
      // Only cards sharing this grid can collide on it: a rune and a unit may
      // hold the same cell, because they are cells of different zones.
      if (id === self || this.arrangeZoneOf(id) !== where) continue;
      taken.add(`${cell[0]},${cell[1]}`);
    }
    if (!taken.has(`${wanted[0]},${wanted[1]}`)) return wanted;

    // Outward from the wanted cell, so a card lands as near as it can to where
    // it was actually put.
    let best: [number, number] | null = null;
    let bestDist = Infinity;
    for (let gx = -grid.cols; gx <= grid.cols; gx++) {
      for (let gz = -grid.rows; gz <= grid.rows; gz++) {
        if (taken.has(`${gx},${gz}`)) continue;
        const d = Math.hypot(gx - wanted[0], gz - wanted[1]);
        if (d < bestDist) { bestDist = d; best = [gx, gz]; }
      }
    }
    return best ?? wanted;
  }

  /** The legal activations of one object's own abilities. */
  private activationsOn(
    id: ObjectId, actions: readonly Action[],
  ): Extract<Action, { t: 'activate' }>[] {
    return actions.filter((a): a is Extract<Action, { t: 'activate' }> =>
      a.t === 'activate' && a.object === id);
  }

  /** Whether this is one of the player's own cards, sitting in their base. */
  private restingInMyBase(id: ObjectId): boolean {
    const object = this.session.state.objects[id];
    if (!object || object.controller !== this.session.human) return false;
    if (object.zone.kind !== 'board') return false;
    return object.location?.kind === 'base' && object.location.player === this.session.human;
  }

  private beginMove(id: ObjectId, moves: Extract<Action, { t: 'move' }>[]): void {
    this.pending = { kind: 'moveUnit', objects: [id], options: moves };
    this.refreshMove();
  }

  /**
   * Uses the ability on a card, asking only what actually needs asking.
   *
   * A card can print more than one activated ability, and each can be legal
   * against more than one target. Those are two different questions, so they
   * are asked separately: which ability first, then which target -- grouping by
   * `ability` is what keeps a two-ability card from offering its targets as if
   * they all belonged to one.
   */
  private useAbility(id: ObjectId, uses: Extract<Action, { t: 'activate' }>[]): void {
    // Nothing legal yet usually means the resources are still standing in
    // untapped runes. The same search that pays for a card pays for an ability
    // -- and it has to, now that runes cannot be tapped by hand.
    if (uses.length === 0) {
      const paid = this.payForAbility(id);
      if (!paid) { this.explainNoAbility(id); return; }
      uses = paid;
    }

    const byAbility = new Map<string, Extract<Action, { t: 'activate' }>[]>();
    for (const a of uses) {
      const group = byAbility.get(a.ability);
      if (group) group.push(a);
      else byAbility.set(a.ability, [a]);
    }
    if (byAbility.size > 1) { this.openMenu(id, this.abilityItems(id, uses)); return; }

    const only = [...byAbility.values()][0]!;
    if (this.offerTargets(only, 'use it on')) return;
    this.submit(only[0]!);
  }

  /** One menu item per distinct ability on the card. */
  private abilityItems(
    id: ObjectId, uses: Extract<Action, { t: 'activate' }>[],
  ): MenuItem[] {
    const object = this.session.state.objects[id];
    const abilities = object ? abilitiesOn(this.session.state, this.session.rules, object) : [];
    const seen = new Set<string>();
    const items: MenuItem[] = [];
    for (const use of uses) {
      if (seen.has(use.ability)) continue;
      seen.add(use.ability);
      const ability = abilities.find((a) => a.kind === 'activated' && a.key === use.ability);
      const from = ability && ability.kind === 'activated' ? ability.from : undefined;
      const cost = ability && ability.kind === 'activated' ? ability.cost : undefined;
      items.push({
        label: from ? from[0]!.toUpperCase() + from.slice(1) : 'Use ability',
        sub: costLabel(cost),
        action: use,
      });
    }
    return items;
  }

  /**
   * Taps runes toward an ability on `id`, and returns what that makes legal.
   *
   * Runs on a clone first, so an ability that turns out to be unaffordable
   * costs the player nothing -- the same discipline the card path uses.
   */
  private payForAbility(id: ObjectId): Extract<Action, { t: 'activate' }>[] | null {
    const object = this.session.state.objects[id];
    if (!object) return null;
    for (const ability of abilitiesOn(this.session.state, this.session.rules, object)) {
      if (ability.kind !== 'activated') continue;
      const path = findPaymentPath(this.session.state, this.session.rules,
        { kind: 'activate', object: id, ability: ability.key });
      if (!path) continue;
      for (const step of path) this.session.submit(step);
      const now = this.activationsOn(id, this.session.humanActions());
      if (now.length > 0) return now;
    }
    return null;
  }

  /** Every declined click says why; an ability that is not usable is no exception. */
  private explainNoAbility(id: ObjectId): void {
    const object = this.session.state.objects[id];
    const def = object && this.session.rules.index.get(object.card);
    const name = def ? def.name : 'That card';
    const has = object
      && abilitiesOn(this.session.state, this.session.rules, object)
        .some((a) => a.kind === 'activated');
    this.callbacks.onPrompt(
      !has
        ? `${name} has no ability to use.`
        : object?.exhausted
          ? `${name} is exhausted.`
          : `You cannot use ${name}'s ability right now.`,
    );
  }

  /** Opens the card's gesture menu; `null` on an item means "move". */
  private openMenu(id: ObjectId, items: MenuItem[]): void {
    const object = this.session.state.objects[id];
    const def = object && this.session.rules.index.get(object.card);
    this.menu = { object: id, title: def ? def.name : 'Choose', items };
    this.callbacks.onState();
  }

  /** Answers the gesture menu. Public so the HUD drives the same path. */
  pickMenu(index: number): void {
    const menu = this.menu;
    if (!menu) return;
    const item = menu.items[index];
    this.menu = null;
    if (!item) { this.callbacks.onState(); return; }
    if (item.action === null) {
      const moves = this.session.humanActions().filter(
        (a): a is Extract<Action, { t: 'move' }> =>
          a.t === 'move' && a.objects.includes(menu.object));
      if (moves.length > 0) this.beginMove(menu.object, moves);
      this.callbacks.onState();
      return;
    }
    this.submit(item.action);
  }

  /**
   * Looks for a sequence of resource activations that makes `object` playable.
   *
   * Runs entirely on a cloned state so a card that turns out to be unaffordable
   * costs the player nothing. Breadth is bounded by the number of untapped
   * resource sources, which is small; a greedy walk is enough because every
   * activation only ever adds to the pool.
   */
  private findPaymentPath(object: ObjectId): Action[] | null {
    return findPaymentPath(this.session.state, this.session.rules, object);
  }

  /** Runs the action the main button chose. */
  submitPrimary(action: Action): void {
    this.submit(action);
  }

  /**
   * 355.3 / 424. Answers the question an effect stopped to ask.
   *
   * Public so the HUD's overlay drives the same path everything else does --
   * the answer is an action like any other, and goes through the same
   * `legalActions` check.
   */
  answerChoice(pick: number): void {
    if (!this.session.waitingOnHuman) return;
    const answer = this.session.humanActions().find(
      (a) => a.t === 'choose' && a.pick === pick,
    );
    if (answer) this.submit(answer);
  }

  /**
   * Offers a target choice when several actions differ only in what they aim
   * at, and returns whether it did.
   *
   * `legalActions` enumerates one action per target because targets are priced
   * before the card is on the chain (809, 356), so the whole of "aiming" on
   * this side is deciding which of those actions to submit. Without this the
   * client took the first, which is why every spell seemed to pick for itself.
   */
  private offerTargets(options: Action[], verb: string): boolean {
    const aimed = options.filter(
      (a): a is Extract<Action, { t: 'play' | 'activate' }> =>
        (a.t === 'play' || a.t === 'activate') && (a.targets?.length ?? 0) > 0,
    );
    if (aimed.length < 2) return false;
    // Only a real choice if the target sets actually differ.
    const distinct = new Set(aimed.map((a) => JSON.stringify(a.targets ?? [])));
    if (distinct.size < 2) return false;

    const choices = new Map<ObjectId, Action>();
    for (const a of aimed) {
      // The first target is the one the click names; a spell that takes two
      // still resolves to one action, so aiming the first settles the rest.
      const head = a.targets?.[0];
      if (head === undefined || choices.has(head)) continue;
      if (!this.actors.has(head)) continue;
      choices.set(head, a);
    }
    // Targets with no actor on the table -- a card in a hand or a deck --
    // cannot be clicked, so there is nothing to ask.
    if (choices.size < 2) return false;

    const source = options[0]!;
    const object = source.t === 'play' || source.t === 'activate'
      ? source.object : (aimed[0]!.object);
    this.pending = { kind: 'chooseTarget', object, options: aimed };
    this.targetChoices = choices;
    this.callbacks.onPrompt(`Choose a target to ${verb}.`);
    return true;
  }

  /** 108. Opens a pile for reading, or says why it cannot be read. */
  private inspectPile(slot: Slot): void {
    const state = this.session.state;
    const mine = slot.owner === this.session.human;
    const whose = mine ? 'Your' : "Opponent's";

    if (slot.role === 'trash') {
      const cards = zoneList(state, 'trash', slot.owner)
        .map((id) => state.objects[id])
        .filter((o): o is NonNullable<typeof o> => !!o)
        .map((o) => o.card)
        .reverse();
      this.inspecting = { title: `${whose} trash -- ${cards.length} cards`, cards };
      this.callbacks.onInspect(this.inspecting);
      return;
    }

    // Which deck was clicked: they sit in their own printed slots, and the
    // rune deck is the one nearer the runes strip.
    const runeSide = Math.abs(slot.position[2] - zoneFor('runeDeck', mine).z) < 0.2;
    const zone = runeSide ? 'runeDeck' : 'mainDeck';
    const n = zoneList(state, zone, slot.owner).length;
    const name = zone === 'runeDeck' ? 'Rune Deck' : 'Main Deck';
    this.callbacks.onPrompt(
      `${whose} ${name}: ${n} card${n === 1 ? '' : 's'} left. Its order is hidden.`,
    );
  }

  /** Closes the pile view, for the overlay's own dismiss. */
  closeInspect(): void {
    if (!this.inspecting) return;
    this.inspecting = null;
    this.callbacks.onInspect(null);
  }

  private submit(action: Action): void {
    // The marks go, and so does what the panel was showing: `picked` outlives
    // the mulligan otherwise, and the first card tapped in the game proper
    // would be read as the *second* tap on an already-selected one.
    if (action.t === 'mulligan') { this.mulliganPicks.clear(); this.picked = null; }

    // A carried play has not been paid for yet. The runes are spent here, at
    // the drop, and only for the play that is actually being made -- which is
    // what lets a drag be called off for nothing.
    const carry = this.carry;
    if (carry && action.t === 'play' && action.object === carry.object) {
      for (const step of carry.path) this.session.submit(step);
    }

    this.cancel();
    this.session.submit(action);
  }

  private cancel(): void {
    this.pending = { kind: 'none' };
    this.dragging = null;
    this.carry = null;
    this.picked = null;
    this.menu = null;
    this.dropTargets = [];
    this.moveJoinable.clear();
    this.targetChoices.clear();
    this.callbacks.onPrompt(this.prompt());
  }

  endTurn(): void {
    if (!this.session.waitingOnHuman) return;
    const actions = this.session.humanActions();
    const end = actions.find((a) => a.t === 'endTurn') ?? actions.find((a) => a.t === 'pass');
    if (end) this.submit(end);
  }

  /** Public so the HUD's buttons can drive the same paths as clicking. */
  passPriority(): void {
    if (!this.session.waitingOnHuman) return;
    const pass = this.session.humanActions().find((a) => a.t === 'pass');
    if (pass) this.submit(pass);
  }

  /**
   * 465.2. Confirms the combat damage assignment the engine has proposed.
   *
   * While the engine waits on this, it offers exactly one action and no pass,
   * so any other button press is refused and the match cannot go on.
   */
  confirmDamage(): void {
    if (!this.session.waitingOnHuman) return;
    const assign = this.session.humanActions().find((a) => a.t === 'assignDamage');
    if (assign) this.submit(assign);
  }

  /**
   * The action the main button should take right now.
   *
   * The engine decides what is legal, so the button asks it rather than
   * guessing from the board: reading `state.combat` and assuming "pass" is what
   * left the game stuck while damage was waiting to be assigned.
   */
  primaryAction(): Action | null {
    if (!this.session.waitingOnHuman) return null;

    // The mulligan's action carries the player's own selection, which lives
    // here rather than in the legal-action list.
    if (this.session.state.waiting.kind === 'mulligan') {
      return { t: 'mulligan', cards: [...this.mulliganPicks] };
    }
    return primaryActionFrom(this.session.humanActions());
  }

  /** How many cards are marked for replacement right now. */
  get mulliganCount(): number {
    return this.mulliganPicks.size;
  }

  private prompt(): string {
    const state = this.session.state;
    if (state.waiting.kind === 'gameOver') {
      return state.winner === this.session.human ? 'You win.' : 'You lose.';
    }
    if (!this.session.waitingOnHuman) return 'Opponent is thinking…';
    if (state.waiting.kind === 'mulligan') {
      const n = this.mulliganPicks.size;
      return n === 0
        ? 'Mulligan: click up to two cards to replace, or keep your hand.'
        : `Replacing ${n} card${n === 1 ? '' : 's'} — click again to unpick.`;
    }
    if (state.waiting.kind === 'assignDamage') return 'Assigning combat damage…';
    // 355.3 / 424. The overlay carries the question itself; the rail says what
    // the table is waiting for, so a player who looks down still knows.
    if (state.waiting.kind === 'choose') {
      return state.pendingChoice?.prompt ?? 'Make your choice.';
    }
    if (state.combat) return 'Showdown — play an Action, or pass.';
    if (state.openState === 'closed') return 'Something is on the chain — react or pass.';
    return 'Your turn.';
  }

  get pendingKind(): Pending['kind'] {
    return this.pending.kind;
  }
}

// -- geometry helpers --------------------------------------------------------

function pointInQuad(p: [number, number], quad: [number, number][]): boolean {
  // Winding test; the quad is convex so consistent sign means inside.
  let sign = 0;
  for (let i = 0; i < quad.length; i++) {
    const a = quad[i]!;
    const b = quad[(i + 1) % quad.length]!;
    const cross = (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
    const s = Math.sign(cross);
    if (s === 0) continue;
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

const hashSeed = (id: number): number => {
  let h = Math.imul(id ^ 0x27d4eb2d, 0x85ebca6b);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
};

export { clamp };
